/**
 * ClusterScope mock data module.
 *
 * Two complete, distinct demo datasets: ANSEM and FARTCOIN.
 * All data is UI-only — no backend, no real on-chain analysis.
 */

export type ClusterId = string;
export type Quadrant = string;

export interface ClusterBehavioralSummary {
  likelyRole: string;
  tradingStyle: string;
  accuracy: string;
  sharedBehavior: string;
}

export interface ClusterDefinition {
  id: ClusterId;
  name: string;
  categoryLabel: string;
  color: string;
  age: string;
  quadrant: Quadrant;
  clusterPnl: string;
  clusterWinRate: number;
  hubWalletId: string;
  walletCount: number;
  hub: {
    totalPnl: string;
    winRate: number;
    avgHold: string;
    trades: string;
  };
  frequentTokens: string[];
  confidence: number;
  confidenceDetails: string[];
  behavioralSummary: ClusterBehavioralSummary;
}

export interface GraphNodeDef {
  id: string;
  clusterId: ClusterId;
  isHub: boolean;
  label: string;
  fundingSource?: string;
  fundingMethod?: string;
  balance?: string;
  realizedPnl?: string;
  unrealizedPnl?: string;
  winRate?: number;
  tradeCount?: number;
  avgHoldTime?: string;
  largestPosition?: string;
  connectedWalletCount?: number;
  firstSeen?: string;
  lastActive?: string;
  riskIndicators?: string[];
  confidenceScore?: number;
}

export interface GraphEdgeDef {
  source: string;
  target: string;
  clusterId: ClusterId;
}

export interface FundingWalletNodeDef {
  id: string;
  label: string;
  exchange: string;
  color: string;
}

export interface FundingTrailDef {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  amount: string;
  date: string;
}

export interface WalletNetworkNode {
  id: string;
  label: string;
  type: 'funding' | 'frequent' | 'cluster-member' | 'counterparty';
  pnl?: string;
  winRate?: number;
  relationshipStrength?: number;
}

export interface WalletNetworkEdge {
  source: string;
  target: string;
}

export interface WalletIntelligenceData {
  address: string;
  investigationSummary: string;
  summary: {
    totalPnl: string;
    winRate: number;
    totalTrades: number;
    avgHoldTime: string;
    totalVolume: string;
  };
  funding: {
    source: string;
    method: string;
    totalIn: string;
    totalOut: string;
  };
  connectedWallets: { address: string; relationship: string; strength: number }[];
  recentTransactions: { date: string; type: string; token: string; amount: string; pnl: string }[];
  riskIndicators: string[];
  behavioralAnalysis: {
    tradingStyle: string;
    timePreference: string;
    tokenConcentration: string;
    avgEntrySize: string;
    largestWin: string;
    largestLoss: string;
    profitFactor: number;
    sharpeRatio: number;
  };
  network: {
    nodes: WalletNetworkNode[];
    edges: WalletNetworkEdge[];
  };
}

export interface TokenInfo {
  symbol: string;
  name: string;
  price: string;
  change: string;
  logo?: string;
  marketCap?: string;
  liquidity?: string;
  volume24h?: string;
}

export interface Dataset {
  id: string;
  token: TokenInfo;
  clusters: ClusterDefinition[];
  clusterMap: Record<ClusterId, ClusterDefinition>;
  graphNodes: GraphNodeDef[];
  graphNodeMap: Record<string, GraphNodeDef>;
  graphEdges: GraphEdgeDef[];
  fundingWalletNodes: FundingWalletNodeDef[];
  fundingWalletMap: Record<string, FundingWalletNodeDef>;
  fundingTrails: FundingTrailDef[];
  tryTokens: string[];
  tryWallets: string[];
  landingStats: {
    capitalTraced: { value: number; prefix: string; suffix: string; decimals: number; label: string };
    walletsMapped: { value: number; prefix: string; suffix: string; decimals: number; label: string };
    activeClusters: { value: number; prefix: string; suffix: string; decimals: number; label: string };
    avgConfidence: { value: number; prefix: string; suffix: string; decimals: number; label: string };
  };
  walletIntelligenceMap: Record<string, WalletIntelligenceData>;
}

export interface ScanContext {
  mode: 'token' | 'wallet';
  label: string;
}

// ── Helpers ──

function makeWalletMeta(
  label: string,
  overrides: Partial<Omit<GraphNodeDef, 'id' | 'clusterId' | 'isHub' | 'label'>> = {},
): Omit<GraphNodeDef, 'id' | 'clusterId' | 'isHub'> {
  return {
    label,
    fundingSource: overrides.fundingSource ?? 'Binance',
    fundingMethod: overrides.fundingMethod ?? 'USDT Transfer',
    balance: overrides.balance ?? `${(Math.random() * 80 + 10).toFixed(1)}K SOL`,
    realizedPnl: overrides.realizedPnl ?? `$${(Math.random() * 120 + 10).toFixed(1)}K`,
    unrealizedPnl: overrides.unrealizedPnl ?? `$${(Math.random() * 60 - 10).toFixed(1)}K`,
    winRate: overrides.winRate ?? Math.round(Math.random() * 40 + 45),
    tradeCount: overrides.tradeCount ?? Math.round(Math.random() * 2000 + 200),
    avgHoldTime: overrides.avgHoldTime ?? `${(Math.random() * 10 + 0.5).toFixed(1)}m`,
    largestPosition: overrides.largestPosition ?? 'SOL',
    connectedWalletCount: overrides.connectedWalletCount ?? Math.round(Math.random() * 8 + 2),
    firstSeen: overrides.firstSeen ?? '2024-01-15',
    lastActive: overrides.lastActive ?? '2h ago',
    riskIndicators: overrides.riskIndicators ?? ['High velocity', 'CEX withdrawal pattern'],
    confidenceScore: overrides.confidenceScore ?? Math.round(Math.random() * 15 + 82),
    ...overrides,
  };
}

function buildClusterMap(clusters: ClusterDefinition[]): Record<ClusterId, ClusterDefinition> {
  return clusters.reduce((acc, c) => { acc[c.id] = c; return acc; }, {} as Record<ClusterId, ClusterDefinition>);
}

function buildGraphNodeMap(nodes: GraphNodeDef[]): Record<string, GraphNodeDef> {
  return nodes.reduce((acc, n) => { acc[n.id] = n; return acc; }, {} as Record<string, GraphNodeDef>);
}

function buildFundingWalletMap(nodes: FundingWalletNodeDef[]): Record<string, FundingWalletNodeDef> {
  return nodes.reduce((acc, n) => { acc[n.id] = n; return acc; }, {} as Record<string, FundingWalletNodeDef>);
}

// ── DexScreener Integration ──

export interface DexScreenerTokenInfo {
  name: string;
  symbol: string;
  price: string;
  change: string;
  marketCap: string;
  liquidity: string;
  volume24h: string;
  logo?: string;
}

export async function fetchDexScreenerToken(address: string): Promise<DexScreenerTokenInfo | null> {
  try {
    const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${address}`);
    if (!res.ok) return null;
    const data = await res.json();
    const pairs: Array<{
      chainId?: string;
      quoteToken?: { address?: string; symbol?: string };
      baseToken?: { symbol?: string; name?: string; address?: string; icon?: string };
      priceUsd?: string;
      priceChange?: { h24?: string };
      marketCap?: string;
      fdv?: string;
      volume?: { h24?: string };
      liquidity?: { usd?: string };
      info?: { imageUrl?: string };
    }> = data?.pairs ?? [];

    const solPairs = pairs.filter((p) => {
      const isSolana = p.chainId === 'solana';
      const isWrappedSol = p.quoteToken?.address === 'So11111111111111111111111111111111111111112';
      return isSolana || isWrappedSol;
    });

    const best = solPairs.length > 0
      ? solPairs.sort((a, b) => {
          const aLiq = parseFloat(a.liquidity?.usd ?? '0');
          const bLiq = parseFloat(b.liquidity?.usd ?? '0');
          return bLiq - aLiq;
        })[0]
      : (pairs.length > 0 ? pairs[0] : null);

    if (!best) return null;

    const price = parseFloat(best.priceUsd ?? '0');
    let priceStr: string;
    if (price >= 1) {
      priceStr = `$${price.toFixed(2)}`;
    } else if (price >= 0.01) {
      priceStr = `$${price.toFixed(4)}`;
    } else if (price >= 0.0001) {
      priceStr = `$${price.toFixed(6)}`;
    } else {
      priceStr = `$${price.toExponential(2)}`;
    }

    const change = parseFloat(best.priceChange?.h24 ?? '0');
    const changeStr = `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;

    const marketCap = parseFloat(best.marketCap ?? best.fdv ?? '0');
    const marketCapStr = marketCap >= 1_000_000_000
      ? `$${(marketCap / 1_000_000_000).toFixed(2)}B`
      : marketCap >= 1_000_000
        ? `$${(marketCap / 1_000_000).toFixed(2)}M`
        : `$${marketCap.toFixed(0)}`;

    const liquidity = parseFloat(best.liquidity?.usd ?? '0');
    const liquidityStr = liquidity >= 1_000_000
      ? `$${(liquidity / 1_000_000).toFixed(2)}M`
      : `$${liquidity.toFixed(0)}`;

    const volume24h = parseFloat(best.volume?.h24 ?? '0');
    const volumeStr = volume24h >= 1_000_000
      ? `$${(volume24h / 1_000_000).toFixed(2)}M`
      : `$${volume24h.toFixed(0)}`;

    const logo = best.info?.imageUrl ?? best.baseToken?.icon ?? undefined;
    const symbol = best.baseToken?.symbol ?? '???';
    const name = best.baseToken?.name ?? symbol;

    return {
      name,
      symbol,
      price: priceStr,
      change: changeStr,
      marketCap: marketCapStr,
      liquidity: liquidityStr,
      volume24h: volumeStr,
      logo,
    };
  } catch {
    return null;
  }
}

// ── Generic Dataset Generator ──

const GENERIC_CLUSTER_NAMES = ['Smart Money', 'Retail Flow', 'Whale Accumulators', 'Exit Liquidity'];
const GENERIC_CLUSTER_COLORS = ['#a78bfa', '#14e0b0', '#f472b6', '#fb923c'];
const GENERIC_EXCHANGES = ['Binance', 'Coinbase', 'Kraken', 'OKX', 'Bybit', 'MEXC'];
const GENERIC_EXCHANGE_COLORS: Record<string, string> = {
  Binance: '#f0b90b',
  Coinbase: '#0052ff',
  Kraken: '#5b50e3',
  OKX: '#7bed9f',
  Bybit: '#f0a90b',
  MEXC: '#14e0b0',
};

const GENERIC_ADDRESS_PREFIXES = [
  '7HxP', 'Dm4Q', 'K9Lm', 'T8Xa', '3RfN', 'Bw2Y', 'P7Vc', 'Jq5W', 'E1St', 'Gz8K',
  '4LuM', 'Hn3P', 'Xy9Q', 'Ac6R', 'Vs2T', 'Fj4W', 'Nk7B', 'Yd1C', 'Zt5E', 'Gh9J',
  'M3Ka', 'Q8Lr', 'U4Ns', 'W6Dv', 'Ie2O', 'P5Hu', 'R1Fx', 'S9Gy', 'T4Jw', 'V7Kz',
];
const GENERIC_ADDRESS_SUFFIXES = [
  'K9Lm', 'T8Xa', '3RfN', 'Bw2Y', 'P7Vc', 'Jq5W', 'E1St', 'Gz8K', '4LuM', 'Hn3P',
  'Xy9Q', 'Ac6R', 'Vs2T', 'Fj4W', 'Nk7B', 'Yd1C', 'Zt5E', 'Gh9J', 'M3Ka', 'Q8Lr',
  'U4Ns', 'W6Dv', 'Ie2O', 'P5Hu', 'R1Fx', 'S9Gy', 'T4Jw', 'V7Kz', '2Ab', '5Cd',
];

function generateGenericAddress(index: number): string {
  const prefix = GENERIC_ADDRESS_PREFIXES[index % GENERIC_ADDRESS_PREFIXES.length];
  const suffix = GENERIC_ADDRESS_SUFFIXES[(index * 3 + 7) % GENERIC_ADDRESS_SUFFIXES.length];
  return `${prefix}...${suffix}`;
}

function hashAddress(address: string): number {
  let hash = 0;
  for (let i = 0; i < address.length; i++) {
    hash = (hash * 31 + address.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

interface WalletIntelTemplate {
  investigationSummary: string;
  summary: WalletIntelligenceData['summary'];
  funding: WalletIntelligenceData['funding'];
  connectedWallets: Omit<WalletIntelligenceData['connectedWallets'][number], 'address'>[];
  recentTransactions: WalletIntelligenceData['recentTransactions'];
  riskIndicators: WalletIntelligenceData['riskIndicators'];
  behavioralAnalysis: WalletIntelligenceData['behavioralAnalysis'];
}

const WALLET_INTEL_TEMPLATES: WalletIntelTemplate[] = [
  // 1. Scalper / High-Frequency
  {
    investigationSummary: 'High-velocity trading bot behavior detected. Thousands of micro-trades with sub-minute holds suggest algorithmic scalping or MEV extraction. Rapid capital rotation with modest per-trade gains but significant cumulative volume.',
    summary: { totalPnl: '$42.3K', winRate: 52, totalTrades: 3847, avgHoldTime: '1.2m', totalVolume: '$12.4M' },
    funding: { source: 'Binance', method: 'API Key', totalIn: '$2.1M', totalOut: '$2.06M' },
    connectedWallets: [
      { relationship: 'Cluster Peer', strength: 94 },
      { relationship: 'Cluster Peer', strength: 89 },
      { relationship: 'Cluster Peer', strength: 82 },
      { relationship: 'Cluster Peer', strength: 76 },
      { relationship: 'Cross-Cluster', strength: 31 },
    ],
    recentTransactions: [
      { date: '2m ago', type: 'Buy', token: 'SOL', amount: '1.2K', pnl: '+$84' },
      { date: '3m ago', type: 'Sell', token: 'BONK', amount: '4.8K', pnl: '-$42' },
      { date: '5m ago', type: 'Buy', token: 'WIF', amount: '2.1K', pnl: '+$128' },
      { date: '8m ago', type: 'Sell', token: 'SOL', amount: '1.5K', pnl: '+$56' },
      { date: '12m ago', type: 'Buy', token: 'JUP', amount: '890', pnl: '-$18' },
    ],
    riskIndicators: ['High velocity', 'Bot-like patterns', 'Rapid rotation', 'MEV suspected', 'API-automated'],
    behavioralAnalysis: {
      tradingStyle: 'Algorithmic scalping with sub-minute holds',
      timePreference: '24/7 automated (no sleep pattern)',
      tokenConcentration: '32% SOL, 28% BONK, rest scattered',
      avgEntrySize: '$450',
      largestWin: '$2.8K',
      largestLoss: '-$1.9K',
      profitFactor: 1.15,
      sharpeRatio: 0.62,
    },
  },
  // 2. Swing Trader
  {
    investigationSummary: 'Disciplined swing trader with strong risk management. Multi-day position holds with clear entry/exit criteria. Consistent profitability across market cycles with low churn.',
    summary: { totalPnl: '$312.8K', winRate: 78, totalTrades: 412, avgHoldTime: '4.2d', totalVolume: '$2.8M' },
    funding: { source: 'Coinbase', method: 'Fiat Onramp', totalIn: '$890.2K', totalOut: '$577.4K' },
    connectedWallets: [
      { relationship: 'Cluster Peer', strength: 92 },
      { relationship: 'Cluster Peer', strength: 87 },
      { relationship: 'Frequent Counterparty', strength: 68 },
      { relationship: 'Cluster Peer', strength: 84 },
      { relationship: 'Frequent Counterparty', strength: 54 },
    ],
    recentTransactions: [
      { date: '1d ago', type: 'Sell', token: 'JUP', amount: '12.4K', pnl: '+$8.2K' },
      { date: '3d ago', type: 'Buy', token: 'RENDER', amount: '8.4K', pnl: '+$4.1K' },
      { date: '5d ago', type: 'Buy', token: 'SOL', amount: '15.2K', pnl: '+$12.8K' },
      { date: '8d ago', type: 'Sell', token: 'PYTH', amount: '6.8K', pnl: '-$2.4K' },
      { date: '12d ago', type: 'Buy', token: 'JTO', amount: '4.2K', pnl: '+$3.1K' },
    ],
    riskIndicators: ['Low velocity', 'Strong discipline', 'CEX linked'],
    behavioralAnalysis: {
      tradingStyle: 'Trend-following swing with technical entries',
      timePreference: 'US market hours (UTC 14:00-22:00)',
      tokenConcentration: '45% in SOL ecosystem',
      avgEntrySize: '$8.2K',
      largestWin: '$34.2K',
      largestLoss: '-$8.1K',
      profitFactor: 2.8,
      sharpeRatio: 2.14,
    },
  },
  // 3. Meme Degen
  {
    investigationSummary: 'Extreme risk appetite with concentrated memecoin exposure. Wild PnL swings characteristic of pump-chasing behavior. High correlation with social sentiment spikes and viral token launches.',
    summary: { totalPnl: '$128.4K', winRate: 38, totalTrades: 892, avgHoldTime: '2.8h', totalVolume: '$4.1M' },
    funding: { source: 'OKX', method: 'USDT Transfer', totalIn: '$1.5M', totalOut: '$1.37M' },
    connectedWallets: [
      { relationship: 'Cluster Peer', strength: 96 },
      { relationship: 'Cluster Peer', strength: 91 },
      { relationship: 'Cluster Peer', strength: 88 },
      { relationship: 'Cluster Peer', strength: 84 },
      { relationship: 'KOL Connection', strength: 42 },
      { relationship: 'Cluster Peer', strength: 79 },
    ],
    recentTransactions: [
      { date: '2h ago', type: 'Buy', token: 'BOME', amount: '124.2K', pnl: '+$34.2K' },
      { date: '5h ago', type: 'Sell', token: 'SLERF', amount: '42.1K', pnl: '-$18.4K' },
      { date: '1d ago', type: 'Buy', token: 'WIF', amount: '28.4K', pnl: '+$12.1K' },
      { date: '1d ago', type: 'Sell', token: 'PEPE', amount: '18.2K', pnl: '-$8.2K' },
      { date: '2d ago', type: 'Buy', token: 'MOODENG', amount: '34.8K', pnl: '+$15.2K' },
    ],
    riskIndicators: ['Extreme volatility', 'Memecoin concentration', 'FOMO pattern', 'Social-driven entries', 'High slippage tolerance', 'Pump chasing'],
    behavioralAnalysis: {
      tradingStyle: 'Viral momentum chasing with YOLO sizing',
      timePreference: 'Chaotic — peaks during viral events',
      tokenConcentration: '82% in memecoins',
      avgEntrySize: '$3.2K',
      largestWin: '$78.4K',
      largestLoss: '-$34.2K',
      profitFactor: 1.45,
      sharpeRatio: 0.38,
    },
  },
  // 4. Whale Accumulator
  {
    investigationSummary: 'Massive position builder with extremely low trade frequency. Multi-week to multi-month holds suggest deep fundamental conviction or strategic accumulation. Funding from major CEX with large initial capital.',
    summary: { totalPnl: '$1.2M', winRate: 91, totalTrades: 48, avgHoldTime: '31.2d', totalVolume: '$5.2M' },
    funding: { source: 'Kraken', method: 'Wire Transfer', totalIn: '$3.2M', totalOut: '$2.0M' },
    connectedWallets: [
      { relationship: 'Family Office', strength: 97 },
      { relationship: 'Family Office', strength: 94 },
      { relationship: 'Institutional Counterparty', strength: 72 },
      { relationship: 'Institutional Counterparty', strength: 68 },
      { relationship: 'Institutional Counterparty', strength: 61 },
    ],
    recentTransactions: [
      { date: '3d ago', type: 'Buy', token: 'SOL', amount: '124.2K', pnl: '+$42.1K' },
      { date: '18d ago', type: 'Buy', token: 'JUP', amount: '67.2K', pnl: '+$18.4K' },
      { date: '34d ago', type: 'Buy', token: 'RENDER', amount: '42.1K', pnl: '+$12.8K' },
      { date: '56d ago', type: 'Sell', token: 'PYTH', amount: '28.4K', pnl: '+$8.2K' },
      { date: '78d ago', type: 'Buy', token: 'JTO', amount: '18.2K', pnl: '+$5.4K' },
    ],
    riskIndicators: ['Whale accumulation', 'Deep conviction', 'CEX linked', 'Low velocity', 'Long hold pattern'],
    behavioralAnalysis: {
      tradingStyle: 'Strategic accumulation with multi-month holds',
      timePreference: 'Early morning UTC (04:00-08:00)',
      tokenConcentration: '68% in SOL ecosystem',
      avgEntrySize: '$42.1K',
      largestWin: '$312.8K',
      largestLoss: '-$28.4K',
      profitFactor: 8.2,
      sharpeRatio: 3.45,
    },
  },
  // 5. Dormant / Low Activity
  {
    investigationSummary: 'Sporadic activity with very long dormancy periods between trades. Wallet shows moderate win rate when active but extremely low overall engagement. Possible backup or cold-storage wallet.',
    summary: { totalPnl: '$18.4K', winRate: 64, totalTrades: 28, avgHoldTime: '56.2d', totalVolume: '$124.2K' },
    funding: { source: 'Unknown', method: 'Peer Transfer', totalIn: '$45.2K', totalOut: '$26.8K' },
    connectedWallets: [
      { relationship: 'Peer Wallet', strength: 88 },
      { relationship: 'Frequent Counterparty', strength: 52 },
    ],
    recentTransactions: [
      { date: '14d ago', type: 'Buy', token: 'SOL', amount: '2.8K', pnl: '+$1.2K' },
      { date: '62d ago', type: 'Sell', token: 'JUP', amount: '1.4K', pnl: '+$840' },
      { date: '124d ago', type: 'Buy', token: 'BONK', amount: '4.2K', pnl: '-$320' },
      { date: '198d ago', type: 'Sell', token: 'SOL', amount: '3.1K', pnl: '+$1.8K' },
      { date: '245d ago', type: 'Buy', token: 'WIF', amount: '1.8K', pnl: '+$420' },
    ],
    riskIndicators: ['Dormancy pattern', 'Low activity', 'Peer funded', 'Long hold pattern'],
    behavioralAnalysis: {
      tradingStyle: 'Sporadic conviction with months between trades',
      timePreference: 'Irregular — no clear pattern',
      tokenConcentration: '54% in blue-chip SOL tokens',
      avgEntrySize: '$2.8K',
      largestWin: '$8.4K',
      largestLoss: '-$3.2K',
      profitFactor: 2.1,
      sharpeRatio: 1.34,
    },
  },
  // 6. Loss Streak / Rekt
  {
    investigationSummary: 'Consistently negative trading performance with high volume and rapid token rotation. Likely chasing losses with increasingly risky positions. Funding from multiple sources suggests capital replenishment after drawdowns.',
    summary: { totalPnl: '-$87.2K', winRate: 28, totalTrades: 1248, avgHoldTime: '45m', totalVolume: '$6.8M' },
    funding: { source: 'Multiple', method: 'Mixed', totalIn: '$2.8M', totalOut: '$2.71M' },
    connectedWallets: [
      { relationship: 'Cluster Peer', strength: 91 },
      { relationship: 'Cluster Peer', strength: 87 },
      { relationship: 'Cluster Peer', strength: 84 },
      { relationship: 'Cluster Peer', strength: 79 },
      { relationship: 'Cluster Peer', strength: 76 },
      { relationship: 'Cluster Peer', strength: 72 },
      { relationship: 'Cross-Cluster', strength: 34 },
      { relationship: 'Cross-Cluster', strength: 28 },
    ],
    recentTransactions: [
      { date: '15m ago', type: 'Buy', token: 'SLERF', amount: '4.2K', pnl: '-$2.8K' },
      { date: '32m ago', type: 'Sell', token: 'BOME', amount: '3.1K', pnl: '-$1.2K' },
      { date: '1h ago', type: 'Buy', token: 'MYRO', amount: '2.8K', pnl: '-$1.8K' },
      { date: '2h ago', type: 'Sell', token: 'WIF', amount: '5.4K', pnl: '+$420' },
      { date: '3h ago', type: 'Buy', token: 'PEPE', amount: '3.8K', pnl: '-$2.1K' },
    ],
    riskIndicators: ['Loss streak', 'High rotation', 'Chasing behavior', 'Capital depletion', 'Multiple funding sources', 'Reckless sizing', 'FOMO pattern'],
    behavioralAnalysis: {
      tradingStyle: 'Emotional rotation with revenge trading tendencies',
      timePreference: 'Panic hours (UTC 18:00-02:00)',
      tokenConcentration: 'Highly diversified (no clear focus)',
      avgEntrySize: '$1.8K',
      largestWin: '$12.4K',
      largestLoss: '-$28.7K',
      profitFactor: 0.42,
      sharpeRatio: -0.78,
    },
  },
  // 7. Arbitrage / MEV
  {
    investigationSummary: 'Lightning-fast execution with near-instantaneous holds and extremely high win rate. Funding from multiple exchanges strongly suggests cross-exchange arbitrage or sandwich/MEV bot behavior. Profits are modest but consistent per trade.',
    summary: { totalPnl: '$98.4K', winRate: 96, totalTrades: 5210, avgHoldTime: '8s', totalVolume: '$18.2M' },
    funding: { source: 'Multiple', method: 'Cross-CEX', totalIn: '$5.2M', totalOut: '$5.1M' },
    connectedWallets: [
      { relationship: 'Arbitrage Cluster', strength: 94 },
      { relationship: 'Arbitrage Cluster', strength: 89 },
      { relationship: 'Arbitrage Cluster', strength: 86 },
      { relationship: 'Exchange Wallet', strength: 72 },
      { relationship: 'Exchange Wallet', strength: 68 },
    ],
    recentTransactions: [
      { date: '8s ago', type: 'Buy', token: 'SOL', amount: '420', pnl: '+$12' },
      { date: '15s ago', type: 'Sell', token: 'USDC', amount: '380', pnl: '+$8' },
      { date: '22s ago', type: 'Buy', token: 'JUP', amount: '290', pnl: '+$18' },
      { date: '31s ago', type: 'Sell', token: 'SOL', amount: '510', pnl: '+$14' },
      { date: '45s ago', type: 'Buy', token: 'USDC', amount: '340', pnl: '+$6' },
    ],
    riskIndicators: ['MEV suspected', 'Cross-exchange arbitrage', 'Bot-like patterns', 'Flash holds', 'High velocity'],
    behavioralAnalysis: {
      tradingStyle: 'Cross-market arbitrage with microsecond holds',
      timePreference: '24/7 continuous (automated)',
      tokenConcentration: 'Stablecoin-heavy (USDC 45%, SOL 30%)',
      avgEntrySize: '$120',
      largestWin: '$1.2K',
      largestLoss: '-$84',
      profitFactor: 1.35,
      sharpeRatio: 1.88,
    },
  },
  // 8. Social / Copy Trader
  {
    investigationSummary: 'Moderate activity with clear timing correlation to known KOL and influencer wallets. Low originality score suggests signal-following or copy-trading behavior. Connected to several high-profile trader wallets.',
    summary: { totalPnl: '$56.2K', winRate: 61, totalTrades: 342, avgHoldTime: '1.8d', totalVolume: '$1.2M' },
    funding: { source: 'Coinbase', method: 'Fiat Onramp', totalIn: '$312.4K', totalOut: '$256.2K' },
    connectedWallets: [
      { relationship: 'Copy Cluster', strength: 92 },
      { relationship: 'Copy Cluster', strength: 88 },
      { relationship: 'Copy Cluster', strength: 84 },
      { relationship: 'Copy Cluster', strength: 79 },
      { relationship: 'KOL Wallet', strength: 48 },
      { relationship: 'KOL Wallet', strength: 42 },
      { relationship: 'KOL Wallet', strength: 38 },
    ],
    recentTransactions: [
      { date: '1h ago', type: 'Buy', token: 'BONK', amount: '8.4K', pnl: '+$2.1K' },
      { date: '3h ago', type: 'Sell', token: 'WIF', amount: '6.2K', pnl: '+$1.8K' },
      { date: '6h ago', type: 'Buy', token: 'JUP', amount: '4.8K', pnl: '-$420' },
      { date: '1d ago', type: 'Sell', token: 'RENDER', amount: '3.2K', pnl: '+$980' },
      { date: '2d ago', type: 'Buy', token: 'SOL', amount: '5.1K', pnl: '+$1.2K' },
    ],
    riskIndicators: ['Copy-trader pattern', 'KOL correlated', 'Low originality', 'Signal latency < 2min', 'Social-driven'],
    behavioralAnalysis: {
      tradingStyle: 'Signal-following with minor position adjustments',
      timePreference: 'Social peak hours (UTC 16:00-00:00)',
      tokenConcentration: 'Mirrors KOL portfolios (diverse)',
      avgEntrySize: '$2.1K',
      largestWin: '$14.2K',
      largestLoss: '-$6.8K',
      profitFactor: 1.72,
      sharpeRatio: 1.15,
    },
  },
  // 9. NFT / Alt Speculator
  {
    investigationSummary: 'Irregular trading cadence with heavy exposure to NFT mints and low-cap altcoins. Large position swings between NFT cycles and token speculation. Low win rate but occasional outsized wins from early mints.',
    summary: { totalPnl: '$34.8K', winRate: 42, totalTrades: 156, avgHoldTime: '8.4d', totalVolume: '$412.1K' },
    funding: { source: 'Magic Eden', method: 'NFT Sales', totalIn: '$124.2K', totalOut: '$89.4K' },
    connectedWallets: [
      { relationship: 'NFT Flipper', strength: 86 },
      { relationship: 'NFT Flipper', strength: 78 },
      { relationship: 'Marketplace', strength: 52 },
    ],
    recentTransactions: [
      { date: '1d ago', type: 'Buy', token: 'BONK', amount: '4.2K', pnl: '-$1.2K' },
      { date: '5d ago', type: 'Sell', token: 'WIF', amount: '2.8K', pnl: '+$3.4K' },
      { date: '12d ago', type: 'Buy', token: 'JUP', amount: '3.1K', pnl: '-$840' },
      { date: '18d ago', type: 'Sell', token: 'SLERF', amount: '5.4K', pnl: '+$8.2K' },
      { date: '24d ago', type: 'Buy', token: 'SOL', amount: '1.8K', pnl: '+$420' },
    ],
    riskIndicators: ['NFT concentration', 'Low-cap speculation', 'Mint chasing', 'Illiquid holdings', 'High variance'],
    behavioralAnalysis: {
      tradingStyle: 'NFT-centric with alt-coin rotation between cycles',
      timePreference: 'Mint drops (UTC 18:00-20:00)',
      tokenConcentration: '38% NFTs, 34% low-cap alts',
      avgEntrySize: '$1.2K',
      largestWin: '$24.2K',
      largestLoss: '-$8.4K',
      profitFactor: 1.28,
      sharpeRatio: 0.72,
    },
  },
  // 10. Market Maker / LP
  {
    investigationSummary: 'Extremely high trade volume with razor-thin per-trade margins. Consistent two-sided quoting behavior across DEX pools. Position holds measured in seconds to minutes. Characteristic of automated market-making or liquidity provision.',
    summary: { totalPnl: '$78.2K', winRate: 68, totalTrades: 8920, avgHoldTime: '22s', totalVolume: '$32.1M' },
    funding: { source: 'Bybit', method: 'Institutional API', totalIn: '$8.2M', totalOut: '$8.12M' },
    connectedWallets: [
      { relationship: 'MM Cluster', strength: 96 },
      { relationship: 'MM Cluster', strength: 92 },
      { relationship: 'DEX Vault', strength: 74 },
      { relationship: 'DEX Vault', strength: 68 },
      { relationship: 'DEX Vault', strength: 62 },
    ],
    recentTransactions: [
      { date: '5s ago', type: 'Buy', token: 'USDC', amount: '1.2K', pnl: '+$4' },
      { date: '12s ago', type: 'Sell', token: 'SOL', amount: '890', pnl: '+$2' },
      { date: '18s ago', type: 'Buy', token: 'JUP', amount: '640', pnl: '+$6' },
      { date: '25s ago', type: 'Sell', token: 'USDC', amount: '1.5K', pnl: '-$1' },
      { date: '34s ago', type: 'Buy', token: 'SOL', amount: '720', pnl: '+$3' },
    ],
    riskIndicators: ['Market maker pattern', 'Two-sided quoting', 'Bot-like patterns', 'DEX concentration', 'Institutional API'],
    behavioralAnalysis: {
      tradingStyle: 'Automated liquidity provision with tight spreads',
      timePreference: '24/7 continuous quoting',
      tokenConcentration: 'USDC/SOL pair (58%), JUP/SOL (22%)',
      avgEntrySize: '$850',
      largestWin: '$4.2K',
      largestLoss: '-$2.8K',
      profitFactor: 1.18,
      sharpeRatio: 1.45,
    },
  },
  // 11. Newbie / Learning
  {
    investigationSummary: 'Early-stage wallet with small position sizes and inconsistent strategy. Mix of buy-and-hold attempts and panic sells. Growing but still modest trade count. Funding from retail onramp suggests new market participant.',
    summary: { totalPnl: '$4.2K', winRate: 45, totalTrades: 68, avgHoldTime: '12.4d', totalVolume: '$28.4K' },
    funding: { source: 'Coinbase', method: 'Fiat Onramp', totalIn: '$18.2K', totalOut: '$14.0K' },
    connectedWallets: [
      { relationship: 'Peer', strength: 72 },
      { relationship: 'Exchange', strength: 44 },
    ],
    recentTransactions: [
      { date: '2d ago', type: 'Buy', token: 'SOL', amount: '420', pnl: '+$84' },
      { date: '5d ago', type: 'Sell', token: 'JUP', amount: '310', pnl: '-$42' },
      { date: '8d ago', type: 'Buy', token: 'BONK', amount: '520', pnl: '-$18' },
      { date: '14d ago', type: 'Sell', token: 'WIF', amount: '280', pnl: '+$120' },
      { date: '21d ago', type: 'Buy', token: 'SOL', amount: '340', pnl: '+$56' },
    ],
    riskIndicators: ['Inconsistent strategy', 'Small size', 'Panic selling', 'Retail onramp', 'Learning pattern'],
    behavioralAnalysis: {
      tradingStyle: 'Inconsistent mix of holding and panic selling',
      timePreference: 'Evening hours (UTC 20:00-02:00)',
      tokenConcentration: '52% SOL, rest scattered alts',
      avgEntrySize: '$340',
      largestWin: '$1.8K',
      largestLoss: '-$1.2K',
      profitFactor: 1.35,
      sharpeRatio: 0.45,
    },
  },
  // 12. Institutional / Fund
  {
    investigationSummary: 'Institutional-grade execution with very large position sizes and conservative risk metrics. Multi-source funding from major exchanges and custodians. Long-dated positions with occasional rebalancing. Consistent modest alpha generation.',
    summary: { totalPnl: '$456.2K', winRate: 72, totalTrades: 124, avgHoldTime: '18.4d', totalVolume: '$8.9M' },
    funding: { source: 'Multiple', method: 'Custodial Transfer', totalIn: '$5.2M', totalOut: '$4.74M' },
    connectedWallets: [
      { relationship: 'Fund Sub-wallet', strength: 96 },
      { relationship: 'Fund Sub-wallet', strength: 94 },
      { relationship: 'Fund Sub-wallet', strength: 91 },
      { relationship: 'Institutional Counterparty', strength: 68 },
      { relationship: 'Institutional Counterparty', strength: 64 },
      { relationship: 'Institutional Counterparty', strength: 58 },
      { relationship: 'Institutional Counterparty', strength: 52 },
    ],
    recentTransactions: [
      { date: '2d ago', type: 'Buy', token: 'SOL', amount: '42.1K', pnl: '+$12.4K' },
      { date: '8d ago', type: 'Sell', token: 'JUP', amount: '28.4K', pnl: '+$8.2K' },
      { date: '16d ago', type: 'Buy', token: 'RENDER', amount: '18.2K', pnl: '+$4.1K' },
      { date: '28d ago', type: 'Sell', token: 'PYTH', amount: '12.4K', pnl: '+$3.8K' },
      { date: '42d ago', type: 'Buy', token: 'JTO', amount: '8.4K', pnl: '+$2.1K' },
    ],
    riskIndicators: ['Institutional pattern', 'Conservative sizing', 'Multi-source funding', 'Custodial linked', 'Low velocity', 'Rebalancing behavior'],
    behavioralAnalysis: {
      tradingStyle: 'Systematic rebalancing with fundamental conviction',
      timePreference: 'Institutional hours (UTC 13:00-21:00)',
      tokenConcentration: '42% SOL, 28% JUP, rest blue-chip',
      avgEntrySize: '$28.4K',
      largestWin: '$124.2K',
      largestLoss: '-$18.4K',
      profitFactor: 3.8,
      sharpeRatio: 2.68,
    },
  },
];

function buildWalletIntelligenceFromTemplate(address: string, _symbol: string): WalletIntelligenceData {
  const hash = hashAddress(address);
  const template = WALLET_INTEL_TEMPLATES[hash % WALLET_INTEL_TEMPLATES.length];

  const cwAddresses = [
    generateGenericAddress(hash % 30),
    generateGenericAddress((hash + 1) % 30),
    generateGenericAddress((hash + 2) % 30),
    generateGenericAddress((hash + 3) % 30),
    generateGenericAddress((hash + 4) % 30),
    generateGenericAddress((hash + 5) % 30),
    generateGenericAddress((hash + 6) % 30),
    generateGenericAddress((hash + 7) % 30),
  ];

  const connectedWallets = template.connectedWallets.map((w, i) => ({
    ...w,
    address: cwAddresses[i] || generateGenericAddress((hash + i) % 30),
  }));

  const networkNodes: WalletNetworkNode[] = [
    { id: address, label: address, type: 'frequent' },
  ];

  if (template.funding.source !== 'Unknown' && template.funding.source !== 'Multiple') {
    networkNodes.push({
      id: `fw-${template.funding.source.toLowerCase()}`,
      label: `${template.funding.source} Hot`,
      type: 'funding',
    });
  }

  connectedWallets.forEach((w) => {
    networkNodes.push({
      id: w.address,
      label: w.address,
      type: w.relationship.includes('Cluster') || w.relationship.includes('Arbitrage') || w.relationship.includes('MM') || w.relationship.includes('Copy') || w.relationship.includes('NFT') || w.relationship.includes('Fund')
        ? 'cluster-member'
        : 'counterparty',
      pnl: w.strength > 80 ? '+$42.1K' : w.strength > 50 ? '+$18.2K' : '+$8.4K',
      winRate: w.strength,
    });
  });

  const networkEdges: WalletNetworkEdge[] = [];
  if (template.funding.source !== 'Unknown' && template.funding.source !== 'Multiple') {
    networkEdges.push({ source: `fw-${template.funding.source.toLowerCase()}`, target: address });
  }
  connectedWallets.forEach((w) => {
    networkEdges.push({ source: address, target: w.address });
  });

  return {
    address,
    investigationSummary: template.investigationSummary,
    summary: template.summary,
    funding: template.funding,
    connectedWallets,
    recentTransactions: template.recentTransactions,
    riskIndicators: template.riskIndicators,
    behavioralAnalysis: template.behavioralAnalysis,
    network: { nodes: networkNodes, edges: networkEdges },
  };
}

// ── Generic Dataset Templates ──

interface GenericTemplate {
  clusterNames: string[];
  colors: string[];
  membersPerCluster: number;
  pnlHubRange: [number, number];
  pnlMemberRange: [number, number];
  winRateHubRange: [number, number];
  winRateMemberRange: [number, number];
  tradesHubRange: [number, number];
  tradesMemberRange: [number, number];
  holdHubRange: [number, number];
  holdMemberRange: [number, number];
  frequentTokens: string[];
  edgeStyle: 'chain' | 'star' | 'dense' | 'ring';
  fundingTrailsPerCluster: number;
  ageRange: [number, number];
  confidenceRange: [number, number];
  clusterPnlRange: [number, number];
  clusterWinRateRange: [number, number];
  landingStats: { capitalTraced: number; walletsMapped: number; activeClusters: number; avgConfidence: number };
}

const GENERIC_TEMPLATES: GenericTemplate[] = [
  {
    clusterNames: ['Smart Money', 'Retail Flow', 'Whale Accumulators', 'Exit Liquidity'],
    colors: ['#a78bfa', '#14e0b0', '#f472b6', '#fb923c'],
    membersPerCluster: 5,
    pnlHubRange: [20, 200],
    pnlMemberRange: [5, 60],
    winRateHubRange: [60, 90],
    winRateMemberRange: [40, 75],
    tradesHubRange: [100, 1000],
    tradesMemberRange: [50, 500],
    holdHubRange: [0.5, 10],
    holdMemberRange: [0.2, 8],
    frequentTokens: ['SOL', 'JUP', 'BONK', 'WIF', 'RENDER'],
    edgeStyle: 'chain',
    fundingTrailsPerCluster: 2,
    ageRange: [2, 12],
    confidenceRange: [82, 97],
    clusterPnlRange: [50, 500],
    clusterWinRateRange: [40, 80],
    landingStats: { capitalTraced: 1.2, walletsMapped: 24, activeClusters: 4, avgConfidence: 85 },
  },
  {
    clusterNames: ['Fast Flippers', 'Dormant Whales', 'Copy Traders', 'KOL'],
    colors: ['#14e0b0', '#60a5fa', '#a78bfa', '#f472b6'],
    membersPerCluster: 6,
    pnlHubRange: [5, 300],
    pnlMemberRange: [2, 80],
    winRateHubRange: [55, 92],
    winRateMemberRange: [35, 78],
    tradesHubRange: [500, 4000],
    tradesMemberRange: [200, 2500],
    holdHubRange: [0.1, 5],
    holdMemberRange: [0.05, 3],
    frequentTokens: ['BONK', 'WIF', 'PEPE', 'MOODENG', 'SLERF'],
    edgeStyle: 'dense',
    fundingTrailsPerCluster: 3,
    ageRange: [3, 15],
    confidenceRange: [84, 96],
    clusterPnlRange: [30, 600],
    clusterWinRateRange: [35, 85],
    landingStats: { capitalTraced: 2.4, walletsMapped: 28, activeClusters: 4, avgConfidence: 88 },
  },
  {
    clusterNames: ['Whale', 'Developer', 'High Winrate'],
    colors: ['#a78bfa', '#fb923c', '#14e0b0'],
    membersPerCluster: 7,
    pnlHubRange: [50, 500],
    pnlMemberRange: [10, 120],
    winRateHubRange: [60, 95],
    winRateMemberRange: [50, 85],
    tradesHubRange: [50, 500],
    tradesMemberRange: [30, 400],
    holdHubRange: [5, 25],
    holdMemberRange: [3, 20],
    frequentTokens: ['SOL', 'JUP', 'PYTH', 'JTO', 'RENDER'],
    edgeStyle: 'star',
    fundingTrailsPerCluster: 3,
    ageRange: [8, 24],
    confidenceRange: [88, 98],
    clusterPnlRange: [80, 800],
    clusterWinRateRange: [55, 92],
    landingStats: { capitalTraced: 3.1, walletsMapped: 24, activeClusters: 3, avgConfidence: 91 },
  },
  {
    clusterNames: ['Fresh Wallets', 'Retail Momentum', 'Exit Liquidity', 'Smart Money'],
    colors: ['#f472b6', '#fb923c', '#60a5fa', '#a78bfa'],
    membersPerCluster: 6,
    pnlHubRange: [-50, 150],
    pnlMemberRange: [-30, 80],
    winRateHubRange: [30, 70],
    winRateMemberRange: [25, 65],
    tradesHubRange: [200, 2500],
    tradesMemberRange: [100, 1800],
    holdHubRange: [0.2, 3],
    holdMemberRange: [0.1, 2],
    frequentTokens: ['SLERF', 'BOME', 'MEW', 'WIF', 'BONK'],
    edgeStyle: 'ring',
    fundingTrailsPerCluster: 2,
    ageRange: [1, 8],
    confidenceRange: [78, 92],
    clusterPnlRange: [-60, 300],
    clusterWinRateRange: [25, 70],
    landingStats: { capitalTraced: 0.8, walletsMapped: 28, activeClusters: 4, avgConfidence: 82 },
  },
  {
    clusterNames: ['Smart Money', 'High Winrate', 'Fresh Wallets', 'KOL', 'Developer'],
    colors: ['#14e0b0', '#a78bfa', '#f472b6', '#fb923c', '#60a5fa'],
    membersPerCluster: 4,
    pnlHubRange: [10, 250],
    pnlMemberRange: [3, 70],
    winRateHubRange: [45, 88],
    winRateMemberRange: [35, 75],
    tradesHubRange: [150, 1200],
    tradesMemberRange: [80, 900],
    holdHubRange: [1, 12],
    holdMemberRange: [0.5, 8],
    frequentTokens: ['SOL', 'JUP', 'BONK', 'WIF', 'RENDER'],
    edgeStyle: 'dense',
    fundingTrailsPerCluster: 2,
    ageRange: [2, 10],
    confidenceRange: [80, 95],
    clusterPnlRange: [40, 400],
    clusterWinRateRange: [35, 82],
    landingStats: { capitalTraced: 1.5, walletsMapped: 25, activeClusters: 5, avgConfidence: 86 },
  },
];

export function generateGenericDataset(tokenInfo: Partial<TokenInfo> & { symbol: string }): Dataset {
  const symbol = tokenInfo.symbol;
  const name = tokenInfo.name ?? symbol;

  const template = GENERIC_TEMPLATES[Math.floor(Math.random() * GENERIC_TEMPLATES.length)];

  const clusters: ClusterDefinition[] = template.clusterNames.map((n, i) => ({
    id: `generic-${i}`,
    name: n,
    categoryLabel: n.toUpperCase(),
    color: template.colors[i % template.colors.length],
    age: `${Math.round(Math.random() * (template.ageRange[1] - template.ageRange[0]) + template.ageRange[0])}w`,
    quadrant: `q${i}`,
    clusterPnl: `$${(Math.random() * (template.clusterPnlRange[1] - template.clusterPnlRange[0]) + template.clusterPnlRange[0]).toFixed(1)}K`,
    clusterWinRate: Math.round(Math.random() * (template.clusterWinRateRange[1] - template.clusterWinRateRange[0]) + template.clusterWinRateRange[0]),
    hubWalletId: `generic-${i}-hub`,
    walletCount: template.membersPerCluster + 1,
    hub: {
      totalPnl: `$${(Math.random() * (template.pnlHubRange[1] - template.pnlHubRange[0]) + template.pnlHubRange[0]).toFixed(1)}K`,
      winRate: Math.round(Math.random() * (template.winRateHubRange[1] - template.winRateHubRange[0]) + template.winRateHubRange[0]),
      avgHold: `${(Math.random() * (template.holdHubRange[1] - template.holdHubRange[0]) + template.holdHubRange[0]).toFixed(1)}d`,
      trades: `${Math.round(Math.random() * (template.tradesHubRange[1] - template.tradesHubRange[0]) + template.tradesHubRange[0])}`,
    },
    frequentTokens: [symbol, ...template.frequentTokens.slice(0, 4)],
    confidence: Math.round(Math.random() * (template.confidenceRange[1] - template.confidenceRange[0]) + template.confidenceRange[0]),
    confidenceDetails: [
      `Entry timing correlation: ${Math.round(Math.random() * 15 + 82)}%`,
      `${template.membersPerCluster} same-cluster wallets`,
      `${Math.max(3, Math.floor(template.membersPerCluster * 0.7))} shared tokens detected`,
      `Common CEX origin: ${GENERIC_EXCHANGES[i % GENERIC_EXCHANGES.length]}`,
    ],
    behavioralSummary: {
      likelyRole: n,
      tradingStyle: `${n} trading patterns observed with ${Math.round(Math.random() * 40 + 40)}% coordination`,
      accuracy: `${Math.round(Math.random() * 30 + 55)}% win rate across cluster`,
      sharedBehavior: `${Math.round(Math.random() * 15 + 80)}% synchronized activity within ${Math.round(Math.random() * 60 + 5)}s`,
    },
  }));

  const graphNodes: GraphNodeDef[] = [];
  let addrIndex = 0;
  clusters.forEach((cluster, ci) => {
    const hubId = `generic-${ci}-hub`;
    const hubAddr = generateGenericAddress(addrIndex++);
    graphNodes.push({
      id: hubId,
      clusterId: cluster.id,
      isHub: true,
      ...makeWalletMeta(hubAddr, {
        fundingSource: GENERIC_EXCHANGES[ci % GENERIC_EXCHANGES.length],
        balance: `${(Math.random() * 100 + 20).toFixed(1)}K SOL`,
        realizedPnl: `$${(Math.random() * (template.pnlHubRange[1] - template.pnlHubRange[0]) + template.pnlHubRange[0]).toFixed(1)}K`,
        winRate: Math.round(Math.random() * (template.winRateHubRange[1] - template.winRateHubRange[0]) + template.winRateHubRange[0]),
        tradeCount: Math.round(Math.random() * (template.tradesHubRange[1] - template.tradesHubRange[0]) + template.tradesHubRange[0]),
      }),
    });
    for (let i = 0; i < template.membersPerCluster; i++) {
      const id = `generic-${ci}-${i}`;
      const memberAddr = generateGenericAddress(addrIndex++);
      graphNodes.push({
        id,
        clusterId: cluster.id,
        isHub: false,
        ...makeWalletMeta(memberAddr, {
          fundingSource: GENERIC_EXCHANGES[(ci + i) % GENERIC_EXCHANGES.length],
          balance: `${(Math.random() * 50 + 5).toFixed(1)}K SOL`,
          realizedPnl: `$${(Math.random() * (template.pnlMemberRange[1] - template.pnlMemberRange[0]) + template.pnlMemberRange[0]).toFixed(1)}K`,
          winRate: Math.round(Math.random() * (template.winRateMemberRange[1] - template.winRateMemberRange[0]) + template.winRateMemberRange[0]),
          tradeCount: Math.round(Math.random() * (template.tradesMemberRange[1] - template.tradesMemberRange[0]) + template.tradesMemberRange[0]),
        }),
      });
    }
  });

  const graphEdges: GraphEdgeDef[] = [];
  clusters.forEach((cluster, ci) => {
    const hubId = `generic-${ci}-hub`;
    const nodeIds = graphNodes.filter((n) => n.clusterId === cluster.id && !n.isHub).map((n) => n.id);

    // Hub to all members
    nodeIds.forEach((nid) => {
      graphEdges.push({ source: hubId, target: nid, clusterId: cluster.id });
    });

    // Member-to-member edges based on template style
    if (template.edgeStyle === 'chain') {
      for (let i = 0; i < nodeIds.length - 1; i++) {
        graphEdges.push({ source: nodeIds[i], target: nodeIds[i + 1], clusterId: cluster.id });
      }
    } else if (template.edgeStyle === 'ring') {
      for (let i = 0; i < nodeIds.length; i++) {
        const next = (i + 1) % nodeIds.length;
        graphEdges.push({ source: nodeIds[i], target: nodeIds[next], clusterId: cluster.id });
      }
    } else if (template.edgeStyle === 'dense') {
      for (let i = 0; i < nodeIds.length; i++) {
        for (let j = i + 1; j < Math.min(i + 3, nodeIds.length); j++) {
          graphEdges.push({ source: nodeIds[i], target: nodeIds[j], clusterId: cluster.id });
        }
      }
    }
    // 'star' style: only hub-to-member edges, no member-to-member
  });

  const fundingWalletNodes: FundingWalletNodeDef[] = GENERIC_EXCHANGES.map((ex) => ({
    id: `fw-${ex.toLowerCase()}`,
    label: `${ex} Hot`,
    exchange: ex,
    color: GENERIC_EXCHANGE_COLORS[ex] ?? '#a1a1aa',
  }));

  const fundingTrails: FundingTrailDef[] = [];
  let trailId = 0;
  clusters.forEach((cluster, ci) => {
    const hubId = `generic-${ci}-hub`;
    const nodeIds = graphNodes.filter((n) => n.clusterId === cluster.id && !n.isHub).map((n) => n.id);
    const ex = GENERIC_EXCHANGES[ci % GENERIC_EXCHANGES.length];
    const exId = `fw-${ex.toLowerCase()}`;
    fundingTrails.push({
      id: `gt-${trailId++}`,
      sourceNodeId: exId,
      targetNodeId: hubId,
      amount: `${(Math.random() * 80 + 20).toFixed(1)}K SOL`,
      date: 'Oct 12',
    });
    const trailCount = Math.min(template.fundingTrailsPerCluster, nodeIds.length);
    for (let i = 0; i < trailCount; i++) {
      fundingTrails.push({
        id: `gt-${trailId++}`,
        sourceNodeId: exId,
        targetNodeId: nodeIds[i],
        amount: `${(Math.random() * 40 + 5).toFixed(1)}K SOL`,
        date: 'Oct 12',
      });
    }
  });

  const walletIntel: Record<string, WalletIntelligenceData> = {};
  const intelAddresses = graphNodes.slice(0, Math.min(3, graphNodes.length)).map((n) => n.label);
  intelAddresses.forEach((addr) => {
    walletIntel[addr] = buildWalletIntelligenceFromTemplate(addr, symbol);
  });

  const token: TokenInfo = {
    symbol,
    name,
    price: tokenInfo.price ?? '$0.00',
    change: tokenInfo.change ?? '0.0%',
    logo: tokenInfo.logo,
    marketCap: tokenInfo.marketCap,
    liquidity: tokenInfo.liquidity,
    volume24h: tokenInfo.volume24h,
  };

  return buildDataset(
    symbol.toLowerCase(),
    token,
    clusters,
    graphNodes,
    graphEdges,
    fundingWalletNodes,
    fundingTrails,
    [symbol, 'SOL', 'JUP'],
    intelAddresses,
    {
      capitalTraced: { value: template.landingStats.capitalTraced, prefix: '$', suffix: 'M+', decimals: 1, label: 'CAPITAL TRACED' },
      walletsMapped: { value: template.landingStats.walletsMapped, prefix: '', suffix: '', decimals: 0, label: 'WALLETS MAPPED' },
      activeClusters: { value: template.landingStats.activeClusters, prefix: '', suffix: '', decimals: 0, label: 'ACTIVE CLUSTERS' },
      avgConfidence: { value: template.landingStats.avgConfidence, prefix: '', suffix: '%', decimals: 0, label: 'AVG CONFIDENCE' },
    },
    walletIntel,
  );
}

// ── ANSEM Dataset ──

const ANSEM_CLUSTERS: ClusterDefinition[] = [
  {
    id: 'early-accumulators',
    name: 'Early Accumulators',
    categoryLabel: 'EARLY POSITION BUILDING',
    color: '#14e0b0',
    age: '9w',
    quadrant: 'q0',
    clusterPnl: '$412.7K',
    clusterWinRate: 71.3,
    hubWalletId: 'ea-hub',
    walletCount: 6,
    hub: { totalPnl: '$198.4K', winRate: 82.1, avgHold: '6.2d', trades: '412' },
    frequentTokens: ['ANSEM', 'JUP', 'PYTH', 'JTO', 'RENDER'],
    confidence: 94,
    confidenceDetails: [
      'Entry timing correlation: 94%',
      '5 same-cluster wallets',
      '4 shared tokens detected',
      'Common CEX origin: Kraken (Acc #8842)',
    ],
    behavioralSummary: {
      likelyRole: 'Early Accumulator',
      tradingStyle: 'Patient position builder with layered entries',
      accuracy: 'Strong — 82.1% win rate across 412 trades',
      sharedBehavior: '94% synchronized accumulation windows across 5 wallets',
    },
  },
  {
    id: 'smart-money',
    name: 'Smart Money',
    categoryLabel: 'HIGH-CONVICTION TRADERS',
    color: '#a78bfa',
    age: '6w',
    quadrant: 'q1',
    clusterPnl: '$892.1K',
    clusterWinRate: 84.7,
    hubWalletId: 'sm-hub',
    walletCount: 7,
    hub: { totalPnl: '$456.2K', winRate: 91.3, avgHold: '2.1d', trades: '678' },
    frequentTokens: ['ANSEM', 'WIF', 'BONK', 'PNUT', 'MOODENG'],
    confidence: 98,
    confidenceDetails: [
      'Entry timing correlation: 98%',
      '6 same-cluster wallets',
      '5 shared tokens detected',
      'Common CEX origin: Binance (Acc #2156)',
    ],
    behavioralSummary: {
      likelyRole: 'Alpha Hunter',
      tradingStyle: 'High-conviction swing with tight risk management',
      accuracy: 'Excellent — 91.3% win rate across 678 trades',
      sharedBehavior: '98% coordinated entry timing within 60s of signal',
    },
  },
  {
    id: 'core-holders',
    name: 'Core Holders',
    categoryLabel: 'LONG-TERM CONVICTION',
    color: '#fb923c',
    age: '11w',
    quadrant: 'q2',
    clusterPnl: '$156.4K',
    clusterWinRate: 62.1,
    hubWalletId: 'ch-hub',
    walletCount: 6,
    hub: { totalPnl: '$78.3K', winRate: 68.4, avgHold: '14.2d', trades: '198' },
    frequentTokens: ['ANSEM', 'JUP', 'RENDER', 'HNT', 'PYTH'],
    confidence: 87,
    confidenceDetails: [
      'Entry timing correlation: 87%',
      '5 same-cluster wallets',
      '4 shared tokens detected',
      'Common CEX origin: Kraken (Acc #1104)',
    ],
    behavioralSummary: {
      likelyRole: 'Long-Term Holder',
      tradingStyle: 'Conviction-based holding with minimal churn',
      accuracy: 'Moderate — 68.4% win rate across 198 trades',
      sharedBehavior: '87% holding-pattern overlap across 5 wallets',
    },
  },
  {
    id: 'distribution-wallets',
    name: 'Distribution Wallets',
    categoryLabel: 'LIQUIDITY DISTRIBUTION',
    color: '#f472b6',
    age: '4w',
    quadrant: 'q3',
    clusterPnl: '$78.3K',
    clusterWinRate: 55.4,
    hubWalletId: 'ds-hub',
    walletCount: 7,
    hub: { totalPnl: '$42.1K', winRate: 61.2, avgHold: '1.8d', trades: '356' },
    frequentTokens: ['ANSEM', 'BONK', 'WIF', 'MEW', 'SLERF'],
    confidence: 91,
    confidenceDetails: [
      'Entry timing correlation: 91%',
      '6 same-cluster wallets',
      '5 shared tokens detected',
      'Common CEX origin: Coinbase (Acc #3321)',
    ],
    behavioralSummary: {
      likelyRole: 'Liquidity Distributor',
      tradingStyle: 'Staged exits with volume-spreading tactics',
      accuracy: 'Moderate — 61.2% win rate across 356 trades',
      sharedBehavior: '91% synchronized exit timing across 6 wallets',
    },
  },
  {
    id: 'exit-liquidity',
    name: 'Exit Liquidity',
    categoryLabel: 'EXIT / DUMPS',
    color: '#60a5fa',
    age: '3w',
    quadrant: 'q4',
    clusterPnl: '-$42.1K',
    clusterWinRate: 38.2,
    hubWalletId: 'el-hub',
    walletCount: 6,
    hub: { totalPnl: '-$18.7K', winRate: 42.1, avgHold: '0.6d', trades: '892' },
    frequentTokens: ['ANSEM', 'BOME', 'MYRO', 'SLERF', 'WIF'],
    confidence: 85,
    confidenceDetails: [
      'Entry timing correlation: 85%',
      '5 same-cluster wallets',
      '4 shared tokens detected',
      'Common CEX origin: OKX (Acc #5567)',
    ],
    behavioralSummary: {
      likelyRole: 'Exit Liquidity Provider',
      tradingStyle: 'Rapid rotation and aggressive profit-taking',
      accuracy: 'Weak — 42.1% win rate across 892 trades',
      sharedBehavior: '85% synchronized sell-pressure events across 5 wallets',
    },
  },
];

const ANSEM_EA = {
  hub: makeWalletMeta('8JdP...Xa92', {
    fundingSource: 'Kraken',
    fundingMethod: 'Fiat Onramp',
    balance: '142.3K SOL',
    realizedPnl: '$198.4K',
    unrealizedPnl: '$32.1K',
    winRate: 82,
    tradeCount: 412,
    avgHoldTime: '6.2d',
    largestPosition: 'ANSEM',
    connectedWalletCount: 5,
    firstSeen: '2023-10-12',
    lastActive: '3h ago',
    riskIndicators: ['Accumulation burst', 'CEX linked', 'Long hold pattern'],
    confidenceScore: 94,
  }),
  regulars: [
    makeWalletMeta('2VbS...Hn4W', { fundingSource: 'Kraken', balance: '34.1K SOL', realizedPnl: '$41.2K', winRate: 62, tradeCount: 312 }),
    makeWalletMeta('5TcR...Kd8L', { fundingSource: 'Kraken', balance: '28.7K SOL', realizedPnl: '$28.9K', winRate: 71, tradeCount: 198 }),
    makeWalletMeta('4YfG...Jm2Q', { fundingSource: 'Coinbase', balance: '19.4K SOL', realizedPnl: '$15.4K', winRate: 55, tradeCount: 210 }),
    makeWalletMeta('6UaD...Zx9P', { fundingSource: 'Coinbase', balance: '45.2K SOL', realizedPnl: '$52.1K', winRate: 81, tradeCount: 276 }),
    makeWalletMeta('1WzX...Bc7R', { fundingSource: 'Kraken', balance: '12.8K SOL', realizedPnl: '$8.9K', winRate: 48, tradeCount: 156 }),
  ],
};

const ANSEM_SM = {
  hub: makeWalletMeta('Gj8k...F2Qa', {
    fundingSource: 'Binance',
    fundingMethod: 'USDT Transfer',
    balance: '312.8K SOL',
    realizedPnl: '$456.2K',
    unrealizedPnl: '$78.4K',
    winRate: 91,
    tradeCount: 678,
    avgHoldTime: '2.1d',
    largestPosition: 'ANSEM',
    connectedWalletCount: 6,
    firstSeen: '2023-08-15',
    lastActive: '12m ago',
    riskIndicators: ['Smart-money pattern', 'Tight stops', 'High velocity'],
    confidenceScore: 98,
  }),
  regulars: [
    makeWalletMeta('7NpQ...Lm3K', { fundingSource: 'Binance', balance: '98.2K SOL', realizedPnl: '$124.1K', winRate: 89, tradeCount: 890 }),
    makeWalletMeta('3GhT...Xc8R', { fundingSource: 'Binance', balance: '76.4K SOL', realizedPnl: '$98.7K', winRate: 87, tradeCount: 745 }),
    makeWalletMeta('9JkL...Bn4W', { fundingSource: 'OKX', balance: '45.1K SOL', realizedPnl: '$52.4K', winRate: 81, tradeCount: 398 }),
    makeWalletMeta('5RfD...Mq7P', { fundingSource: 'Binance', balance: '112.3K SOL', realizedPnl: '$156.9K', winRate: 93, tradeCount: 1204 }),
    makeWalletMeta('8CsE...Ht2L', { fundingSource: 'OKX', balance: '34.7K SOL', realizedPnl: '$38.2K', winRate: 79, tradeCount: 512 }),
    makeWalletMeta('2VxN...Jw9R', { fundingSource: 'Binance', balance: '88.9K SOL', realizedPnl: '$112.3K', winRate: 85, tradeCount: 967 }),
  ],
};

const ANSEM_CH = {
  hub: makeWalletMeta('9RtM...Lp7X', {
    fundingSource: 'Kraken',
    fundingMethod: 'Fiat Onramp',
    balance: '56.2K SOL',
    realizedPnl: '$78.3K',
    unrealizedPnl: '$12.4K',
    winRate: 68,
    tradeCount: 198,
    avgHoldTime: '14.2d',
    largestPosition: 'ANSEM',
    connectedWalletCount: 5,
    firstSeen: '2023-11-05',
    lastActive: '6h ago',
    riskIndicators: ['Long hold pattern', 'Accumulation burst', 'Low velocity'],
    confidenceScore: 87,
  }),
  regulars: [
    makeWalletMeta('4BmK...Pc3Q', { fundingSource: 'Kraken', balance: '23.1K SOL', realizedPnl: '$28.4K', winRate: 61, tradeCount: 198 }),
    makeWalletMeta('6YtH...La5W', { fundingSource: 'Kraken', balance: '18.7K SOL', realizedPnl: '$19.4K', winRate: 54, tradeCount: 156 }),
    makeWalletMeta('1DgM...Rk8P', { fundingSource: 'Binance', balance: '31.4K SOL', realizedPnl: '$34.2K', winRate: 63, tradeCount: 267 }),
    makeWalletMeta('3QwA...Xs6L', { fundingSource: 'Kraken', balance: '14.2K SOL', realizedPnl: '$12.1K', winRate: 49, tradeCount: 98 }),
    makeWalletMeta('5FnC...Jb2R', { fundingSource: 'Binance', balance: '22.5K SOL', realizedPnl: '$22.8K', winRate: 58, tradeCount: 178 }),
  ],
};

const ANSEM_DS = {
  hub: makeWalletMeta('3KxN...Bm9R', {
    fundingSource: 'Coinbase',
    fundingMethod: 'ETH Bridge',
    balance: '89.4K SOL',
    realizedPnl: '$42.1K',
    unrealizedPnl: '-$4.1K',
    winRate: 61,
    tradeCount: 356,
    avgHoldTime: '1.8d',
    largestPosition: 'ANSEM',
    connectedWalletCount: 6,
    firstSeen: '2024-01-18',
    lastActive: '45m ago',
    riskIndicators: ['Distribution pattern', 'Staged exits', 'Volume spreading'],
    confidenceScore: 91,
  }),
  regulars: [
    makeWalletMeta('7HpE...Td9K', { fundingSource: 'Coinbase', balance: '22.1K SOL', realizedPnl: '$12.4K', winRate: 58, tradeCount: 310 }),
    makeWalletMeta('9LrB...Wc4M', { fundingSource: 'Coinbase', balance: '15.6K SOL', realizedPnl: '$8.7K', winRate: 52, tradeCount: 198 }),
    makeWalletMeta('2KsF...Yp7N', { fundingSource: 'Binance', balance: '31.2K SOL', realizedPnl: '$19.8K', winRate: 67, tradeCount: 445 }),
    makeWalletMeta('4MnD...Vh3Q', { fundingSource: 'Coinbase', balance: '9.8K SOL', realizedPnl: '$4.1K', winRate: 44, tradeCount: 112 }),
    makeWalletMeta('6PgJ...Bt8R', { fundingSource: 'Binance', balance: '44.5K SOL', realizedPnl: '$31.2K', winRate: 72, tradeCount: 678 }),
    makeWalletMeta('8RkL...Xe5W', { fundingSource: 'Binance', balance: '18.3K SOL', realizedPnl: '$11.5K', winRate: 59, tradeCount: 287 }),
  ],
};

const ANSEM_EL = {
  hub: makeWalletMeta('7QwE...Jk3P', {
    fundingSource: 'OKX',
    fundingMethod: 'Direct SOL',
    balance: '45.2K SOL',
    realizedPnl: '-$18.7K',
    unrealizedPnl: '-$8.2K',
    winRate: 42,
    tradeCount: 892,
    avgHoldTime: '0.6d',
    largestPosition: 'ANSEM',
    connectedWalletCount: 5,
    firstSeen: '2024-02-22',
    lastActive: '8m ago',
    riskIndicators: ['Dump pattern', 'Rapid rotation', 'High velocity'],
    confidenceScore: 85,
  }),
  regulars: [
    makeWalletMeta('1TvH...Cm2P', { fundingSource: 'OKX', balance: '12.4K SOL', realizedPnl: '-$4.2K', winRate: 38, tradeCount: 412 }),
    makeWalletMeta('3WxN...Ja9L', { fundingSource: 'OKX', balance: '8.7K SOL', realizedPnl: '-$3.1K', winRate: 35, tradeCount: 298 }),
    makeWalletMeta('5YzB...Kd3R', { fundingSource: 'Binance', balance: '18.2K SOL', realizedPnl: '-$6.8K', winRate: 41, tradeCount: 456 }),
    makeWalletMeta('7AcE...Lf8Q', { fundingSource: 'OKX', balance: '6.3K SOL', realizedPnl: '-$2.4K', winRate: 32, tradeCount: 187 }),
    makeWalletMeta('9DeF...Mg4P', { fundingSource: 'Binance', balance: '14.1K SOL', realizedPnl: '-$5.3K', winRate: 39, tradeCount: 324 }),
  ],
};

const ANSEM_GRAPH_NODES: GraphNodeDef[] = [
  { id: 'ea-hub', clusterId: 'early-accumulators', isHub: true, ...ANSEM_EA.hub },
  ...ANSEM_EA.regulars.map((meta, i) => ({ id: `ea-${i + 1}`, clusterId: 'early-accumulators' as ClusterId, isHub: false, ...meta })),
  { id: 'sm-hub', clusterId: 'smart-money', isHub: true, ...ANSEM_SM.hub },
  ...ANSEM_SM.regulars.map((meta, i) => ({ id: `sm-${i + 1}`, clusterId: 'smart-money' as ClusterId, isHub: false, ...meta })),
  { id: 'ch-hub', clusterId: 'core-holders', isHub: true, ...ANSEM_CH.hub },
  ...ANSEM_CH.regulars.map((meta, i) => ({ id: `ch-${i + 1}`, clusterId: 'core-holders' as ClusterId, isHub: false, ...meta })),
  { id: 'ds-hub', clusterId: 'distribution-wallets', isHub: true, ...ANSEM_DS.hub },
  ...ANSEM_DS.regulars.map((meta, i) => ({ id: `ds-${i + 1}`, clusterId: 'distribution-wallets' as ClusterId, isHub: false, ...meta })),
  { id: 'el-hub', clusterId: 'exit-liquidity', isHub: true, ...ANSEM_EL.hub },
  ...ANSEM_EL.regulars.map((meta, i) => ({ id: `el-${i + 1}`, clusterId: 'exit-liquidity' as ClusterId, isHub: false, ...meta })),
];

const ANSEM_GRAPH_EDGES: GraphEdgeDef[] = [
  // Early Accumulators
  { source: 'ea-hub', target: 'ea-1', clusterId: 'early-accumulators' },
  { source: 'ea-hub', target: 'ea-2', clusterId: 'early-accumulators' },
  { source: 'ea-hub', target: 'ea-3', clusterId: 'early-accumulators' },
  { source: 'ea-hub', target: 'ea-4', clusterId: 'early-accumulators' },
  { source: 'ea-hub', target: 'ea-5', clusterId: 'early-accumulators' },
  { source: 'ea-1', target: 'ea-2', clusterId: 'early-accumulators' },
  { source: 'ea-3', target: 'ea-4', clusterId: 'early-accumulators' },
  { source: 'ea-4', target: 'ea-5', clusterId: 'early-accumulators' },
  // Smart Money
  { source: 'sm-hub', target: 'sm-1', clusterId: 'smart-money' },
  { source: 'sm-hub', target: 'sm-2', clusterId: 'smart-money' },
  { source: 'sm-hub', target: 'sm-3', clusterId: 'smart-money' },
  { source: 'sm-hub', target: 'sm-4', clusterId: 'smart-money' },
  { source: 'sm-hub', target: 'sm-5', clusterId: 'smart-money' },
  { source: 'sm-hub', target: 'sm-6', clusterId: 'smart-money' },
  { source: 'sm-1', target: 'sm-2', clusterId: 'smart-money' },
  { source: 'sm-3', target: 'sm-4', clusterId: 'smart-money' },
  { source: 'sm-5', target: 'sm-6', clusterId: 'smart-money' },
  // Core Holders
  { source: 'ch-hub', target: 'ch-1', clusterId: 'core-holders' },
  { source: 'ch-hub', target: 'ch-2', clusterId: 'core-holders' },
  { source: 'ch-hub', target: 'ch-3', clusterId: 'core-holders' },
  { source: 'ch-hub', target: 'ch-4', clusterId: 'core-holders' },
  { source: 'ch-hub', target: 'ch-5', clusterId: 'core-holders' },
  { source: 'ch-1', target: 'ch-2', clusterId: 'core-holders' },
  { source: 'ch-2', target: 'ch-3', clusterId: 'core-holders' },
  { source: 'ch-3', target: 'ch-4', clusterId: 'core-holders' },
  { source: 'ch-4', target: 'ch-5', clusterId: 'core-holders' },
  // Distribution Wallets
  { source: 'ds-hub', target: 'ds-1', clusterId: 'distribution-wallets' },
  { source: 'ds-hub', target: 'ds-2', clusterId: 'distribution-wallets' },
  { source: 'ds-hub', target: 'ds-3', clusterId: 'distribution-wallets' },
  { source: 'ds-hub', target: 'ds-4', clusterId: 'distribution-wallets' },
  { source: 'ds-hub', target: 'ds-5', clusterId: 'distribution-wallets' },
  { source: 'ds-hub', target: 'ds-6', clusterId: 'distribution-wallets' },
  { source: 'ds-1', target: 'ds-2', clusterId: 'distribution-wallets' },
  { source: 'ds-3', target: 'ds-4', clusterId: 'distribution-wallets' },
  { source: 'ds-5', target: 'ds-6', clusterId: 'distribution-wallets' },
  // Exit Liquidity
  { source: 'el-hub', target: 'el-1', clusterId: 'exit-liquidity' },
  { source: 'el-hub', target: 'el-2', clusterId: 'exit-liquidity' },
  { source: 'el-hub', target: 'el-3', clusterId: 'exit-liquidity' },
  { source: 'el-hub', target: 'el-4', clusterId: 'exit-liquidity' },
  { source: 'el-hub', target: 'el-5', clusterId: 'exit-liquidity' },
  { source: 'el-1', target: 'el-2', clusterId: 'exit-liquidity' },
  { source: 'el-2', target: 'el-3', clusterId: 'exit-liquidity' },
  { source: 'el-3', target: 'el-4', clusterId: 'exit-liquidity' },
  { source: 'el-4', target: 'el-5', clusterId: 'exit-liquidity' },
];

const ANSEM_FUNDING_WALLETS: FundingWalletNodeDef[] = [
  { id: 'fw-kraken', label: 'Kraken Hot', exchange: 'Kraken', color: '#5b50e3' },
  { id: 'fw-coinbase', label: 'Coinbase Hot', exchange: 'Coinbase', color: '#0052ff' },
  { id: 'fw-binance', label: 'Binance Hot', exchange: 'Binance', color: '#f0b90b' },
  { id: 'fw-okx', label: 'OKX Hot', exchange: 'OKX', color: '#7bed9f' },
  { id: 'fw-bybit', label: 'Bybit Hot', exchange: 'Bybit', color: '#f0a90b' },
  { id: 'fw-mexc', label: 'MEXC Hot', exchange: 'MEXC', color: '#14e0b0' },
];

const ANSEM_FUNDING_TRAILS: FundingTrailDef[] = [
  // Kraken -> Early Accumulators
  { id: 'a-ft-1', sourceNodeId: 'fw-kraken', targetNodeId: 'ea-hub', amount: '45.2K SOL', date: 'Oct 12' },
  { id: 'a-ft-2', sourceNodeId: 'fw-kraken', targetNodeId: 'ea-1', amount: '12.1K SOL', date: 'Oct 12' },
  { id: 'a-ft-3', sourceNodeId: 'fw-kraken', targetNodeId: 'ea-2', amount: '8.4K SOL', date: 'Oct 12' },
  // Coinbase -> Early Accumulators
  { id: 'a-ft-4', sourceNodeId: 'fw-coinbase', targetNodeId: 'ea-3', amount: '15.2K SOL', date: 'Nov 03' },
  { id: 'a-ft-5', sourceNodeId: 'fw-coinbase', targetNodeId: 'ea-4', amount: '11.8K SOL', date: 'Nov 03' },
  // Binance -> Smart Money
  { id: 'a-ft-6', sourceNodeId: 'fw-binance', targetNodeId: 'sm-hub', amount: '88.4K SOL', date: 'Aug 15' },
  { id: 'a-ft-7', sourceNodeId: 'fw-binance', targetNodeId: 'sm-1', amount: '34.2K SOL', date: 'Aug 15' },
  { id: 'a-ft-8', sourceNodeId: 'fw-binance', targetNodeId: 'sm-2', amount: '22.1K SOL', date: 'Aug 15' },
  // OKX -> Smart Money
  { id: 'a-ft-9', sourceNodeId: 'fw-okx', targetNodeId: 'sm-3', amount: '19.3K SOL', date: 'Sep 22' },
  { id: 'a-ft-10', sourceNodeId: 'fw-okx', targetNodeId: 'sm-4', amount: '15.7K SOL', date: 'Sep 22' },
  // Bybit -> Core Holders
  { id: 'a-ft-11', sourceNodeId: 'fw-bybit', targetNodeId: 'ch-hub', amount: '28.4K SOL', date: 'Nov 05' },
  { id: 'a-ft-12', sourceNodeId: 'fw-bybit', targetNodeId: 'ch-1', amount: '12.8K SOL', date: 'Nov 05' },
  { id: 'a-ft-13', sourceNodeId: 'fw-bybit', targetNodeId: 'ch-2', amount: '9.4K SOL', date: 'Nov 05' },
  // Binance -> Core Holders
  { id: 'a-ft-14', sourceNodeId: 'fw-binance', targetNodeId: 'ch-3', amount: '18.2K SOL', date: 'Dec 10' },
  // Coinbase -> Distribution Wallets
  { id: 'a-ft-15', sourceNodeId: 'fw-coinbase', targetNodeId: 'ds-hub', amount: '22.1K SOL', date: 'Jan 18' },
  { id: 'a-ft-16', sourceNodeId: 'fw-coinbase', targetNodeId: 'ds-1', amount: '8.4K SOL', date: 'Jan 18' },
  { id: 'a-ft-17', sourceNodeId: 'fw-coinbase', targetNodeId: 'ds-2', amount: '6.1K SOL', date: 'Jan 18' },
  // Binance -> Distribution Wallets
  { id: 'a-ft-18', sourceNodeId: 'fw-binance', targetNodeId: 'ds-3', amount: '14.2K SOL', date: 'Feb 05' },
  { id: 'a-ft-19', sourceNodeId: 'fw-binance', targetNodeId: 'ds-4', amount: '11.8K SOL', date: 'Feb 05' },
  // OKX -> Exit Liquidity
  { id: 'a-ft-20', sourceNodeId: 'fw-okx', targetNodeId: 'el-hub', amount: '18.2K SOL', date: 'Feb 22' },
  { id: 'a-ft-21', sourceNodeId: 'fw-okx', targetNodeId: 'el-1', amount: '7.4K SOL', date: 'Feb 22' },
  { id: 'a-ft-22', sourceNodeId: 'fw-okx', targetNodeId: 'el-2', amount: '5.1K SOL', date: 'Feb 22' },
  // MEXC -> Exit Liquidity
  { id: 'a-ft-23', sourceNodeId: 'fw-mexc', targetNodeId: 'el-3', amount: '12.8K SOL', date: 'Mar 01' },
  { id: 'a-ft-24', sourceNodeId: 'fw-mexc', targetNodeId: 'el-4', amount: '8.2K SOL', date: 'Mar 01' },
  { id: 'a-ft-25', sourceNodeId: 'fw-mexc', targetNodeId: 'el-5', amount: '6.4K SOL', date: 'Mar 01' },
];

const ANSEM_WALLET_INTEL: Record<string, WalletIntelligenceData> = {
  '8JdP...Xa92': {
    address: '8JdP...Xa92',
    investigationSummary: 'This wallet has interacted with 18 unique wallets, received initial funding from Kraken Hot, maintains a 82.1% realized win rate, and exhibits trading behaviour consistent with early smart-money accumulation.',
    summary: { totalPnl: '$198.4K', winRate: 82, totalTrades: 412, avgHoldTime: '6.2d', totalVolume: '$3.2M' },
    funding: { source: 'Kraken', method: 'Fiat Onramp', totalIn: '$652.1K', totalOut: '$453.7K' },
    connectedWallets: [
      { address: '2VbS...Hn4W', relationship: 'Cluster Peer', strength: 96 },
      { address: '5TcR...Kd8L', relationship: 'Cluster Peer', strength: 89 },
      { address: '4YfG...Jm2Q', relationship: 'Cluster Peer', strength: 82 },
      { address: '6UaD...Zx9P', relationship: 'Cluster Peer', strength: 91 },
      { address: '1WzX...Bc7R', relationship: 'Cluster Peer', strength: 74 },
      { address: 'Gj8k...F2Qa', relationship: 'Cross-Cluster', strength: 34 },
    ],
    recentTransactions: [
      { date: '3h ago', type: 'Buy', token: 'ANSEM', amount: '45.2K', pnl: '+$12.4K' },
      { date: '1d ago', type: 'Buy', token: 'JUP', amount: '12.1K', pnl: '+$3.2K' },
      { date: '2d ago', type: 'Sell', token: 'PYTH', amount: '28.7K', pnl: '-$1.1K' },
      { date: '4d ago', type: 'Buy', token: 'RENDER', amount: '8.4K', pnl: '+$2.8K' },
      { date: '6d ago', type: 'Buy', token: 'JTO', amount: '15.3K', pnl: '+$4.5K' },
    ],
    riskIndicators: ['Accumulation burst', 'CEX linked', 'Long hold pattern', 'High velocity'],
    behavioralAnalysis: {
      tradingStyle: 'Patient position builder with layered entries',
      timePreference: 'Off-peak hours (UTC 02:00-06:00)',
      tokenConcentration: '68% in ANSEM ecosystem',
      avgEntrySize: '$8.2K',
      largestWin: '$34.2K',
      largestLoss: '-$6.8K',
      profitFactor: 3.2,
      sharpeRatio: 2.14,
    },
    network: {
      nodes: [
        { id: '8JdP...Xa92', label: '8JdP...Xa92', type: 'frequent' },
        { id: 'fw-kraken', label: 'Kraken Hot', type: 'funding' },
        { id: '2VbS...Hn4W', label: '2VbS...Hn4W', type: 'cluster-member', pnl: '+$41.2K', winRate: 62 },
        { id: '5TcR...Kd8L', label: '5TcR...Kd8L', type: 'cluster-member', pnl: '+$28.9K', winRate: 71 },
        { id: '6UaD...Zx9P', label: '6UaD...Zx9P', type: 'cluster-member', pnl: '+$52.1K', winRate: 81 },
        { id: 'Gj8k...F2Qa', label: 'Gj8k...F2Qa', type: 'counterparty', pnl: '+$456.2K', winRate: 91 },
      ],
      edges: [
        { source: 'fw-kraken', target: '8JdP...Xa92' },
        { source: '8JdP...Xa92', target: '2VbS...Hn4W' },
        { source: '8JdP...Xa92', target: '5TcR...Kd8L' },
        { source: '8JdP...Xa92', target: '6UaD...Zx9P' },
        { source: '8JdP...Xa92', target: 'Gj8k...F2Qa' },
      ],
    },
  },
  'Gj8k...F2Qa': {
    address: 'Gj8k...F2Qa',
    investigationSummary: 'This wallet has interacted with 24 unique wallets, received initial funding from Binance Hot, maintains a 91.3% realized win rate, and exhibits trading behaviour consistent with high-conviction smart-money alpha hunting.',
    summary: { totalPnl: '$456.2K', winRate: 91, totalTrades: 678, avgHoldTime: '2.1d', totalVolume: '$5.8M' },
    funding: { source: 'Binance', method: 'USDT Transfer', totalIn: '$1.2M', totalOut: '$743.8K' },
    connectedWallets: [
      { address: '7NpQ...Lm3K', relationship: 'Cluster Peer', strength: 98 },
      { address: '3GhT...Xc8R', relationship: 'Cluster Peer', strength: 94 },
      { address: '9JkL...Bn4W', relationship: 'Cluster Peer', strength: 89 },
      { address: '5RfD...Mq7P', relationship: 'Cluster Peer', strength: 96 },
      { address: '8CsE...Ht2L', relationship: 'Cluster Peer', strength: 87 },
      { address: '2VxN...Jw9R', relationship: 'Cluster Peer', strength: 92 },
      { address: '8JdP...Xa92', relationship: 'Cross-Cluster', strength: 28 },
    ],
    recentTransactions: [
      { date: '12m ago', type: 'Buy', token: 'ANSEM', amount: '67.2K', pnl: '+$18.4K' },
      { date: '2h ago', type: 'Sell', token: 'WIF', amount: '22.1K', pnl: '+$8.2K' },
      { date: '5h ago', type: 'Buy', token: 'BONK', amount: '34.8K', pnl: '+$5.1K' },
      { date: '1d ago', type: 'Sell', token: 'PNUT', amount: '12.4K', pnl: '-$2.3K' },
      { date: '2d ago', type: 'Buy', token: 'MOODENG', amount: '18.7K', pnl: '+$6.8K' },
    ],
    riskIndicators: ['Smart-money pattern', 'Tight stops', 'High velocity', 'CEX linked'],
    behavioralAnalysis: {
      tradingStyle: 'High-conviction swing with tight risk management',
      timePreference: 'Peak hours (UTC 14:00-18:00)',
      tokenConcentration: '54% in memecoins',
      avgEntrySize: '$12.4K',
      largestWin: '$42.1K',
      largestLoss: '-$8.4K',
      profitFactor: 4.1,
      sharpeRatio: 2.78,
    },
    network: {
      nodes: [
        { id: 'Gj8k...F2Qa', label: 'Gj8k...F2Qa', type: 'frequent' },
        { id: 'fw-binance', label: 'Binance Hot', type: 'funding' },
        { id: '7NpQ...Lm3K', label: '7NpQ...Lm3K', type: 'cluster-member', pnl: '+$124.1K', winRate: 89 },
        { id: '5RfD...Mq7P', label: '5RfD...Mq7P', type: 'cluster-member', pnl: '+$156.9K', winRate: 93 },
        { id: '8JdP...Xa92', label: '8JdP...Xa92', type: 'counterparty', pnl: '+$198.4K', winRate: 82 },
      ],
      edges: [
        { source: 'fw-binance', target: 'Gj8k...F2Qa' },
        { source: 'Gj8k...F2Qa', target: '7NpQ...Lm3K' },
        { source: 'Gj8k...F2Qa', target: '5RfD...Mq7P' },
        { source: 'Gj8k...F2Qa', target: '8JdP...Xa92' },
      ],
    },
  },
};

// ── FARTCOIN Dataset ──

const FARTCOIN_CLUSTERS: ClusterDefinition[] = [
  {
    id: 'meme-whales',
    name: 'Meme Whales',
    categoryLabel: 'WHALE ACCUMULATION',
    color: '#f472b6',
    age: '5w',
    quadrant: 'q0',
    clusterPnl: '$1.2M',
    clusterWinRate: 76.8,
    hubWalletId: 'mw-hub',
    walletCount: 6,
    hub: { totalPnl: '$582.1K', winRate: 84.2, avgHold: '3.4d', trades: '1,024' },
    frequentTokens: ['FARTCOIN', 'BONK', 'WIF', 'PEPE', 'MOODENG'],
    confidence: 97,
    confidenceDetails: [
      'Entry timing correlation: 97%',
      '5 same-cluster wallets',
      '5 shared tokens detected',
      'Common CEX origin: Binance (Acc #7782)',
    ],
    behavioralSummary: {
      likelyRole: 'Meme Whale',
      tradingStyle: 'Large-scale meme accumulation with viral timing',
      accuracy: 'Strong — 84.2% win rate across 1,024 trades',
      sharedBehavior: '97% synchronized entry timing across 5 wallets',
    },
  },
  {
    id: 'retail-momentum',
    name: 'Retail Momentum',
    categoryLabel: 'MOMENTUM CHASING',
    color: '#fb923c',
    age: '3w',
    quadrant: 'q1',
    clusterPnl: '$142.8K',
    clusterWinRate: 48.3,
    hubWalletId: 'rm-hub',
    walletCount: 7,
    hub: { totalPnl: '$38.2K', winRate: 52.1, avgHold: '0.8d', trades: '2,456' },
    frequentTokens: ['FARTCOIN', 'SLERF', 'BOME', 'MYRO', 'WIF'],
    confidence: 82,
    confidenceDetails: [
      'Entry timing correlation: 82%',
      '6 same-cluster wallets',
      '4 shared tokens detected',
      'Common CEX origin: Coinbase (Acc #2215)',
    ],
    behavioralSummary: {
      likelyRole: 'Momentum Chaser',
      tradingStyle: 'Late-entry FOMO with rapid exits',
      accuracy: 'Weak — 52.1% win rate across 2,456 trades',
      sharedBehavior: '82% synchronized buy-pressure after 15%+ moves',
    },
  },
  {
    id: 'fast-flippers',
    name: 'Fast Flippers',
    categoryLabel: 'HIGH-FREQUENCY FLIPS',
    color: '#14e0b0',
    age: '7w',
    quadrant: 'q2',
    clusterPnl: '$398.4K',
    clusterWinRate: 72.4,
    hubWalletId: 'ff-hub',
    walletCount: 6,
    hub: { totalPnl: '$198.7K', winRate: 79.1, avgHold: '2.1h', trades: '3,892' },
    frequentTokens: ['FARTCOIN', 'BONK', 'WIF', 'PEPE', 'BOME'],
    confidence: 94,
    confidenceDetails: [
      'Entry timing correlation: 94%',
      '5 same-cluster wallets',
      '5 shared tokens detected',
      'Common CEX origin: OKX (Acc #3341)',
    ],
    behavioralSummary: {
      likelyRole: 'Fast Flipper',
      tradingStyle: 'Rapid in-and-out with sub-hour holds',
      accuracy: 'Strong — 79.1% win rate across 3,892 trades',
      sharedBehavior: '94% coordinated flip timing within 2 minutes',
    },
  },
  {
    id: 'copy-traders',
    name: 'Copy Traders',
    categoryLabel: 'MIRROR TRADING',
    color: '#a78bfa',
    age: '4w',
    quadrant: 'q3',
    clusterPnl: '$78.2K',
    clusterWinRate: 58.7,
    hubWalletId: 'cp-hub',
    walletCount: 7,
    hub: { totalPnl: '$28.4K', winRate: 61.2, avgHold: '4.2h', trades: '1,128' },
    frequentTokens: ['FARTCOIN', 'SLERF', 'MEW', 'WIF', 'BONK'],
    confidence: 88,
    confidenceDetails: [
      'Entry timing correlation: 88%',
      '6 same-cluster wallets',
      '4 shared tokens detected',
      'Common CEX origin: Kraken (Acc #5562)',
    ],
    behavioralSummary: {
      likelyRole: 'Mirror Trader',
      tradingStyle: 'Signal-following with 15-30s latency',
      accuracy: 'Moderate — 61.2% win rate across 1,128 trades',
      sharedBehavior: '88% trade mirroring within 30s of lead wallet',
    },
  },
  {
    id: 'dormant-whales',
    name: 'Dormant Whales',
    categoryLabel: 'LONG-TERM DORMANCY',
    color: '#60a5fa',
    age: '14w',
    quadrant: 'q4',
    clusterPnl: '$312.4K',
    clusterWinRate: 81.2,
    hubWalletId: 'dw-hub',
    walletCount: 6,
    hub: { totalPnl: '$178.2K', winRate: 88.4, avgHold: '21.4d', trades: '124' },
    frequentTokens: ['FARTCOIN', 'BONK', 'JUP', 'RENDER', 'HNT'],
    confidence: 93,
    confidenceDetails: [
      'Entry timing correlation: 93%',
      '5 same-cluster wallets',
      '3 shared tokens detected',
      'Common CEX origin: Binance (Acc #9912)',
    ],
    behavioralSummary: {
      likelyRole: 'Dormant Whale',
      tradingStyle: 'Deep conviction with multi-week holds',
      accuracy: 'Strong — 88.4% win rate across 124 trades',
      sharedBehavior: '93% synchronized re-activation after 30+ day dormancy',
    },
  },
];

const FARTCOIN_MW = {
  hub: makeWalletMeta('Ax4P...Mn7Q', {
    fundingSource: 'Binance',
    fundingMethod: 'USDT Transfer',
    balance: '524.1K SOL',
    realizedPnl: '$582.1K',
    unrealizedPnl: '$124.2K',
    winRate: 84,
    tradeCount: 1024,
    avgHoldTime: '3.4d',
    largestPosition: 'FARTCOIN',
    connectedWalletCount: 5,
    firstSeen: '2023-06-18',
    lastActive: '2h ago',
    riskIndicators: ['Whale accumulation', 'CEX linked', 'High volume'],
    confidenceScore: 97,
  }),
  regulars: [
    makeWalletMeta('Fq9N...Xc2L', { fundingSource: 'Binance', balance: '142.3K SOL', realizedPnl: '$124.1K', winRate: 78, tradeCount: 890 }),
    makeWalletMeta('Gr3M...Wb8P', { fundingSource: 'Binance', balance: '98.4K SOL', realizedPnl: '$87.2K', winRate: 81, tradeCount: 745 }),
    makeWalletMeta('Hs1K...Va6Q', { fundingSource: 'OKX', balance: '67.1K SOL', realizedPnl: '$52.4K', winRate: 72, tradeCount: 398 }),
    makeWalletMeta('Jt4L...Ud9R', { fundingSource: 'Binance', balance: '178.2K SOL', realizedPnl: '$156.9K', winRate: 89, tradeCount: 1204 }),
    makeWalletMeta('Kw7Z...Tc2P', { fundingSource: 'OKX', balance: '54.7K SOL', realizedPnl: '$38.2K', winRate: 76, tradeCount: 512 }),
  ],
};

const FARTCOIN_RM = {
  hub: makeWalletMeta('Bk2R...Lp9X', {
    fundingSource: 'Coinbase',
    fundingMethod: 'ETH Bridge',
    balance: '34.2K SOL',
    realizedPnl: '$38.2K',
    unrealizedPnl: '-$8.4K',
    winRate: 52,
    tradeCount: 2456,
    avgHoldTime: '0.8d',
    largestPosition: 'FARTCOIN',
    connectedWalletCount: 6,
    firstSeen: '2024-03-12',
    lastActive: '15m ago',
    riskIndicators: ['FOMO pattern', 'Late entry', 'Rapid exits'],
    confidenceScore: 82,
  }),
  regulars: [
    makeWalletMeta('Lx3P...Vb9N', { fundingSource: 'Coinbase', balance: '12.1K SOL', realizedPnl: '$8.4K', winRate: 48, tradeCount: 310 }),
    makeWalletMeta('My5R...Wa2K', { fundingSource: 'Coinbase', balance: '8.7K SOL', realizedPnl: '$4.2K', winRate: 42, tradeCount: 198 }),
    makeWalletMeta('Nz7T...Xc4P', { fundingSource: 'Kraken', balance: '15.2K SOL', realizedPnl: '$12.1K', winRate: 56, tradeCount: 445 }),
    makeWalletMeta('Pa2V...Yd8R', { fundingSource: 'Coinbase', balance: '6.8K SOL', realizedPnl: '$2.8K', winRate: 38, tradeCount: 112 }),
    makeWalletMeta('Qb4W...Ze1K', { fundingSource: 'Binance', balance: '22.4K SOL', realizedPnl: '$18.2K', winRate: 62, tradeCount: 678 }),
    makeWalletMeta('Rc6X...Af3P', { fundingSource: 'Binance', balance: '9.3K SOL', realizedPnl: '$6.1K', winRate: 51, tradeCount: 287 }),
  ],
};

const FARTCOIN_FF = {
  hub: makeWalletMeta('Cn8T...Jk3W', {
    fundingSource: 'OKX',
    fundingMethod: 'Direct SOL',
    balance: '187.4K SOL',
    realizedPnl: '$198.7K',
    unrealizedPnl: '$42.1K',
    winRate: 79,
    tradeCount: 3892,
    avgHoldTime: '2.1h',
    largestPosition: 'FARTCOIN',
    connectedWalletCount: 5,
    firstSeen: '2023-09-05',
    lastActive: '4m ago',
    riskIndicators: ['Fast flip pattern', 'High velocity', 'OKX linked'],
    confidenceScore: 94,
  }),
  regulars: [
    makeWalletMeta('Sd8Y...Bg5L', { fundingSource: 'OKX', balance: '67.2K SOL', realizedPnl: '$68.4K', winRate: 76, tradeCount: 890 }),
    makeWalletMeta('Te1Z...Ch7R', { fundingSource: 'OKX', balance: '54.1K SOL', realizedPnl: '$52.1K', winRate: 79, tradeCount: 745 }),
    makeWalletMeta('Uf3A...Dj9P', { fundingSource: 'Binance', balance: '32.8K SOL', realizedPnl: '$28.4K', winRate: 71, tradeCount: 398 }),
    makeWalletMeta('Vg5B...Ek2L', { fundingSource: 'OKX', balance: '78.4K SOL', realizedPnl: '$82.1K', winRate: 84, tradeCount: 1204 }),
    makeWalletMeta('Wh7C...Fm4R', { fundingSource: 'Binance', balance: '24.7K SOL', realizedPnl: '$18.2K', winRate: 74, tradeCount: 512 }),
  ],
};

const FARTCOIN_CP = {
  hub: makeWalletMeta('Dm5V...Hz1R', {
    fundingSource: 'Kraken',
    fundingMethod: 'Fiat Onramp',
    balance: '45.2K SOL',
    realizedPnl: '$28.4K',
    unrealizedPnl: '-$4.2K',
    winRate: 61,
    tradeCount: 1128,
    avgHoldTime: '4.2h',
    largestPosition: 'FARTCOIN',
    connectedWalletCount: 6,
    firstSeen: '2024-02-28',
    lastActive: '32m ago',
    riskIndicators: ['Mirror trading pattern', 'Signal latency < 30s'],
    confidenceScore: 88,
  }),
  regulars: [
    makeWalletMeta('Xi9D...Gn6P', { fundingSource: 'Kraken', balance: '18.2K SOL', realizedPnl: '$12.4K', winRate: 58, tradeCount: 310 }),
    makeWalletMeta('Yk2E...Hp8L', { fundingSource: 'Kraken', balance: '12.4K SOL', realizedPnl: '$7.8K', winRate: 52, tradeCount: 198 }),
    makeWalletMeta('Zm4F...Jq1R', { fundingSource: 'Binance', balance: '22.1K SOL', realizedPnl: '$14.2K', winRate: 64, tradeCount: 445 }),
    makeWalletMeta('An6G...Kr3P', { fundingSource: 'Kraken', balance: '8.4K SOL', realizedPnl: '$4.1K', winRate: 47, tradeCount: 112 }),
    makeWalletMeta('Bp8H...Ls5L', { fundingSource: 'Binance', balance: '28.7K SOL', realizedPnl: '$18.2K', winRate: 68, tradeCount: 678 }),
    makeWalletMeta('Cr1J...Mt7R', { fundingSource: 'Binance', balance: '14.2K SOL', realizedPnl: '$9.8K', winRate: 55, tradeCount: 287 }),
  ],
};

const FARTCOIN_DW = {
  hub: makeWalletMeta('Ep7B...Gd4S', {
    fundingSource: 'Binance',
    fundingMethod: 'USDT Transfer',
    balance: '312.8K SOL',
    realizedPnl: '$178.2K',
    unrealizedPnl: '$56.4K',
    winRate: 88,
    tradeCount: 124,
    avgHoldTime: '21.4d',
    largestPosition: 'FARTCOIN',
    connectedWalletCount: 5,
    firstSeen: '2023-04-22',
    lastActive: '3d ago',
    riskIndicators: ['Dormancy pattern', 'Deep conviction', 'CEX linked'],
    confidenceScore: 93,
  }),
  regulars: [
    makeWalletMeta('Dt3K...Nv9P', { fundingSource: 'Binance', balance: '98.2K SOL', realizedPnl: '$52.1K', winRate: 84, tradeCount: 98 }),
    makeWalletMeta('Ev5L...Pw2R', { fundingSource: 'Binance', balance: '76.4K SOL', realizedPnl: '$42.8K', winRate: 87, tradeCount: 76 }),
    makeWalletMeta('Fx7M...Qy4L', { fundingSource: 'OKX', balance: '45.1K SOL', realizedPnl: '$28.4K', winRate: 79, tradeCount: 45 }),
    makeWalletMeta('Gy9N...Ra6P', { fundingSource: 'Binance', balance: '112.3K SOL', realizedPnl: '$67.9K', winRate: 91, tradeCount: 134 }),
    makeWalletMeta('Hz2P...Sb8L', { fundingSource: 'OKX', balance: '34.7K SOL', realizedPnl: '$18.2K', winRate: 82, tradeCount: 67 }),
  ],
};

const FARTCOIN_GRAPH_NODES: GraphNodeDef[] = [
  { id: 'mw-hub', clusterId: 'meme-whales', isHub: true, ...FARTCOIN_MW.hub },
  ...FARTCOIN_MW.regulars.map((meta, i) => ({ id: `mw-${i + 1}`, clusterId: 'meme-whales' as ClusterId, isHub: false, ...meta })),
  { id: 'rm-hub', clusterId: 'retail-momentum', isHub: true, ...FARTCOIN_RM.hub },
  ...FARTCOIN_RM.regulars.map((meta, i) => ({ id: `rm-${i + 1}`, clusterId: 'retail-momentum' as ClusterId, isHub: false, ...meta })),
  { id: 'ff-hub', clusterId: 'fast-flippers', isHub: true, ...FARTCOIN_FF.hub },
  ...FARTCOIN_FF.regulars.map((meta, i) => ({ id: `ff-${i + 1}`, clusterId: 'fast-flippers' as ClusterId, isHub: false, ...meta })),
  { id: 'cp-hub', clusterId: 'copy-traders', isHub: true, ...FARTCOIN_CP.hub },
  ...FARTCOIN_CP.regulars.map((meta, i) => ({ id: `cp-${i + 1}`, clusterId: 'copy-traders' as ClusterId, isHub: false, ...meta })),
  { id: 'dw-hub', clusterId: 'dormant-whales', isHub: true, ...FARTCOIN_DW.hub },
  ...FARTCOIN_DW.regulars.map((meta, i) => ({ id: `dw-${i + 1}`, clusterId: 'dormant-whales' as ClusterId, isHub: false, ...meta })),
];

const FARTCOIN_GRAPH_EDGES: GraphEdgeDef[] = [
  // Meme Whales
  { source: 'mw-hub', target: 'mw-1', clusterId: 'meme-whales' },
  { source: 'mw-hub', target: 'mw-2', clusterId: 'meme-whales' },
  { source: 'mw-hub', target: 'mw-3', clusterId: 'meme-whales' },
  { source: 'mw-hub', target: 'mw-4', clusterId: 'meme-whales' },
  { source: 'mw-hub', target: 'mw-5', clusterId: 'meme-whales' },
  { source: 'mw-1', target: 'mw-2', clusterId: 'meme-whales' },
  { source: 'mw-3', target: 'mw-4', clusterId: 'meme-whales' },
  { source: 'mw-4', target: 'mw-5', clusterId: 'meme-whales' },
  // Retail Momentum
  { source: 'rm-hub', target: 'rm-1', clusterId: 'retail-momentum' },
  { source: 'rm-hub', target: 'rm-2', clusterId: 'retail-momentum' },
  { source: 'rm-hub', target: 'rm-3', clusterId: 'retail-momentum' },
  { source: 'rm-hub', target: 'rm-4', clusterId: 'retail-momentum' },
  { source: 'rm-hub', target: 'rm-5', clusterId: 'retail-momentum' },
  { source: 'rm-hub', target: 'rm-6', clusterId: 'retail-momentum' },
  { source: 'rm-1', target: 'rm-2', clusterId: 'retail-momentum' },
  { source: 'rm-3', target: 'rm-4', clusterId: 'retail-momentum' },
  { source: 'rm-5', target: 'rm-6', clusterId: 'retail-momentum' },
  // Fast Flippers
  { source: 'ff-hub', target: 'ff-1', clusterId: 'fast-flippers' },
  { source: 'ff-hub', target: 'ff-2', clusterId: 'fast-flippers' },
  { source: 'ff-hub', target: 'ff-3', clusterId: 'fast-flippers' },
  { source: 'ff-hub', target: 'ff-4', clusterId: 'fast-flippers' },
  { source: 'ff-hub', target: 'ff-5', clusterId: 'fast-flippers' },
  { source: 'ff-1', target: 'ff-2', clusterId: 'fast-flippers' },
  { source: 'ff-2', target: 'ff-3', clusterId: 'fast-flippers' },
  { source: 'ff-3', target: 'ff-4', clusterId: 'fast-flippers' },
  { source: 'ff-4', target: 'ff-5', clusterId: 'fast-flippers' },
  // Copy Traders
  { source: 'cp-hub', target: 'cp-1', clusterId: 'copy-traders' },
  { source: 'cp-hub', target: 'cp-2', clusterId: 'copy-traders' },
  { source: 'cp-hub', target: 'cp-3', clusterId: 'copy-traders' },
  { source: 'cp-hub', target: 'cp-4', clusterId: 'copy-traders' },
  { source: 'cp-hub', target: 'cp-5', clusterId: 'copy-traders' },
  { source: 'cp-hub', target: 'cp-6', clusterId: 'copy-traders' },
  { source: 'cp-1', target: 'cp-2', clusterId: 'copy-traders' },
  { source: 'cp-3', target: 'cp-4', clusterId: 'copy-traders' },
  { source: 'cp-5', target: 'cp-6', clusterId: 'copy-traders' },
  // Dormant Whales
  { source: 'dw-hub', target: 'dw-1', clusterId: 'dormant-whales' },
  { source: 'dw-hub', target: 'dw-2', clusterId: 'dormant-whales' },
  { source: 'dw-hub', target: 'dw-3', clusterId: 'dormant-whales' },
  { source: 'dw-hub', target: 'dw-4', clusterId: 'dormant-whales' },
  { source: 'dw-hub', target: 'dw-5', clusterId: 'dormant-whales' },
  { source: 'dw-1', target: 'dw-2', clusterId: 'dormant-whales' },
  { source: 'dw-2', target: 'dw-3', clusterId: 'dormant-whales' },
  { source: 'dw-3', target: 'dw-4', clusterId: 'dormant-whales' },
  { source: 'dw-4', target: 'dw-5', clusterId: 'dormant-whales' },
];

const FARTCOIN_FUNDING_WALLETS: FundingWalletNodeDef[] = [
  { id: 'fw-binance', label: 'Binance Hot', exchange: 'Binance', color: '#f0b90b' },
  { id: 'fw-okx', label: 'OKX Hot', exchange: 'OKX', color: '#7bed9f' },
  { id: 'fw-coinbase', label: 'Coinbase Hot', exchange: 'Coinbase', color: '#0052ff' },
  { id: 'fw-kraken', label: 'Kraken Hot', exchange: 'Kraken', color: '#5b50e3' },
  { id: 'fw-bybit', label: 'Bybit Hot', exchange: 'Bybit', color: '#f0a90b' },
  { id: 'fw-gateio', label: 'Gate.io Hot', exchange: 'Gate.io', color: '#14e0b0' },
];

const FARTCOIN_FUNDING_TRAILS: FundingTrailDef[] = [
  // Binance -> Meme Whales
  { id: 'f-ft-1', sourceNodeId: 'fw-binance', targetNodeId: 'mw-hub', amount: '124.2K SOL', date: 'Jun 18' },
  { id: 'f-ft-2', sourceNodeId: 'fw-binance', targetNodeId: 'mw-1', amount: '42.1K SOL', date: 'Jun 18' },
  { id: 'f-ft-3', sourceNodeId: 'fw-binance', targetNodeId: 'mw-2', amount: '28.4K SOL', date: 'Jun 18' },
  // OKX -> Meme Whales
  { id: 'f-ft-4', sourceNodeId: 'fw-okx', targetNodeId: 'mw-3', amount: '22.1K SOL', date: 'Jul 05' },
  { id: 'f-ft-5', sourceNodeId: 'fw-okx', targetNodeId: 'mw-4', amount: '34.8K SOL', date: 'Jul 05' },
  // Coinbase -> Retail Momentum
  { id: 'f-ft-6', sourceNodeId: 'fw-coinbase', targetNodeId: 'rm-hub', amount: '18.2K SOL', date: 'Mar 12' },
  { id: 'f-ft-7', sourceNodeId: 'fw-coinbase', targetNodeId: 'rm-1', amount: '7.4K SOL', date: 'Mar 12' },
  { id: 'f-ft-8', sourceNodeId: 'fw-coinbase', targetNodeId: 'rm-2', amount: '5.1K SOL', date: 'Mar 12' },
  // Kraken -> Retail Momentum
  { id: 'f-ft-9', sourceNodeId: 'fw-kraken', targetNodeId: 'rm-3', amount: '12.8K SOL', date: 'Apr 02' },
  { id: 'f-ft-10', sourceNodeId: 'fw-kraken', targetNodeId: 'rm-5', amount: '9.4K SOL', date: 'Apr 02' },
  // Binance -> Retail Momentum
  { id: 'f-ft-11', sourceNodeId: 'fw-binance', targetNodeId: 'rm-4', amount: '8.2K SOL', date: 'May 15' },
  // OKX -> Fast Flippers
  { id: 'f-ft-12', sourceNodeId: 'fw-okx', targetNodeId: 'ff-hub', amount: '56.2K SOL', date: 'Sep 05' },
  { id: 'f-ft-13', sourceNodeId: 'fw-okx', targetNodeId: 'ff-1', amount: '22.4K SOL', date: 'Sep 05' },
  { id: 'f-ft-14', sourceNodeId: 'fw-okx', targetNodeId: 'ff-2', amount: '18.7K SOL', date: 'Sep 05' },
  // Binance -> Fast Flippers
  { id: 'f-ft-15', sourceNodeId: 'fw-binance', targetNodeId: 'ff-3', amount: '14.2K SOL', date: 'Oct 12' },
  { id: 'f-ft-16', sourceNodeId: 'fw-binance', targetNodeId: 'ff-4', amount: '28.4K SOL', date: 'Oct 12' },
  // Kraken -> Copy Traders
  { id: 'f-ft-17', sourceNodeId: 'fw-kraken', targetNodeId: 'cp-hub', amount: '22.1K SOL', date: 'Feb 28' },
  { id: 'f-ft-18', sourceNodeId: 'fw-kraken', targetNodeId: 'cp-1', amount: '8.4K SOL', date: 'Feb 28' },
  { id: 'f-ft-19', sourceNodeId: 'fw-kraken', targetNodeId: 'cp-2', amount: '5.1K SOL', date: 'Feb 28' },
  // Binance -> Copy Traders
  { id: 'f-ft-20', sourceNodeId: 'fw-binance', targetNodeId: 'cp-3', amount: '12.8K SOL', date: 'Mar 15' },
  { id: 'f-ft-21', sourceNodeId: 'fw-binance', targetNodeId: 'cp-4', amount: '9.4K SOL', date: 'Mar 15' },
  // Binance -> Dormant Whales
  { id: 'f-ft-22', sourceNodeId: 'fw-binance', targetNodeId: 'dw-hub', amount: '88.4K SOL', date: 'Apr 22' },
  { id: 'f-ft-23', sourceNodeId: 'fw-binance', targetNodeId: 'dw-1', amount: '34.2K SOL', date: 'Apr 22' },
  { id: 'f-ft-24', sourceNodeId: 'fw-binance', targetNodeId: 'dw-2', amount: '28.1K SOL', date: 'Apr 22' },
  // OKX -> Dormant Whales
  { id: 'f-ft-25', sourceNodeId: 'fw-okx', targetNodeId: 'dw-3', amount: '18.2K SOL', date: 'Jun 05' },
  { id: 'f-ft-26', sourceNodeId: 'fw-okx', targetNodeId: 'dw-4', amount: '22.4K SOL', date: 'Jun 05' },
  // Gate.io -> Dormant Whales
  { id: 'f-ft-27', sourceNodeId: 'fw-gateio', targetNodeId: 'dw-5', amount: '12.8K SOL', date: 'Jul 12' },
];

const FARTCOIN_WALLET_INTEL: Record<string, WalletIntelligenceData> = {
  'Ax4P...Mn7Q': {
    address: 'Ax4P...Mn7Q',
    investigationSummary: 'This wallet has interacted with 22 unique wallets, received initial funding from Binance Hot, maintains a 84.2% realized win rate, and exhibits trading behaviour consistent with large-scale meme whale accumulation.',
    summary: { totalPnl: '$582.1K', winRate: 84, totalTrades: 1024, avgHoldTime: '3.4d', totalVolume: '$4.2M' },
    funding: { source: 'Binance', method: 'USDT Transfer', totalIn: '$2.1M', totalOut: '$1.5M' },
    connectedWallets: [
      { address: 'Fq9N...Xc2L', relationship: 'Cluster Peer', strength: 97 },
      { address: 'Gr3M...Wb8P', relationship: 'Cluster Peer', strength: 94 },
      { address: 'Hs1K...Va6Q', relationship: 'Cluster Peer', strength: 89 },
      { address: 'Jt4L...Ud9R', relationship: 'Cluster Peer', strength: 96 },
      { address: 'Kw7Z...Tc2P', relationship: 'Cluster Peer', strength: 91 },
      { address: 'Ep7B...Gd4S', relationship: 'Cross-Cluster', strength: 31 },
    ],
    recentTransactions: [
      { date: '2h ago', type: 'Buy', token: 'FARTCOIN', amount: '124.2K', pnl: '+$34.2K' },
      { date: '5h ago', type: 'Buy', token: 'BONK', amount: '42.1K', pnl: '+$8.4K' },
      { date: '1d ago', type: 'Sell', token: 'WIF', amount: '28.4K', pnl: '+$12.1K' },
      { date: '2d ago', type: 'Buy', token: 'PEPE', amount: '18.2K', pnl: '-$2.3K' },
      { date: '3d ago', type: 'Sell', token: 'MOODENG', amount: '34.8K', pnl: '+$15.2K' },
    ],
    riskIndicators: ['Whale accumulation', 'CEX linked', 'High volume', 'Cluster leader'],
    behavioralAnalysis: {
      tradingStyle: 'Large-scale meme accumulation with viral timing',
      timePreference: 'Peak hours (UTC 12:00-20:00)',
      tokenConcentration: '78% in memecoins',
      avgEntrySize: '$18.2K',
      largestWin: '$56.4K',
      largestLoss: '-$12.8K',
      profitFactor: 3.8,
      sharpeRatio: 2.42,
    },
    network: {
      nodes: [
        { id: 'Ax4P...Mn7Q', label: 'Ax4P...Mn7Q', type: 'frequent' },
        { id: 'fw-binance', label: 'Binance Hot', type: 'funding' },
        { id: 'Fq9N...Xc2L', label: 'Fq9N...Xc2L', type: 'cluster-member', pnl: '+$124.1K', winRate: 78 },
        { id: 'Jt4L...Ud9R', label: 'Jt4L...Ud9R', type: 'cluster-member', pnl: '+$156.9K', winRate: 89 },
        { id: 'Ep7B...Gd4S', label: 'Ep7B...Gd4S', type: 'counterparty', pnl: '+$178.2K', winRate: 88 },
      ],
      edges: [
        { source: 'fw-binance', target: 'Ax4P...Mn7Q' },
        { source: 'Ax4P...Mn7Q', target: 'Fq9N...Xc2L' },
        { source: 'Ax4P...Mn7Q', target: 'Jt4L...Ud9R' },
        { source: 'Ax4P...Mn7Q', target: 'Ep7B...Gd4S' },
      ],
    },
  },
  'Ep7B...Gd4S': {
    address: 'Ep7B...Gd4S',
    investigationSummary: 'This wallet has interacted with 16 unique wallets, received initial funding from Binance Hot, maintains a 88.4% realized win rate, and exhibits trading behaviour consistent with deep-conviction dormant whale re-activation.',
    summary: { totalPnl: '$178.2K', winRate: 88, totalTrades: 124, avgHoldTime: '21.4d', totalVolume: '$1.8M' },
    funding: { source: 'Binance', method: 'USDT Transfer', totalIn: '$892.1K', totalOut: '$713.9K' },
    connectedWallets: [
      { address: 'Dt3K...Nv9P', relationship: 'Cluster Peer', strength: 93 },
      { address: 'Ev5L...Pw2R', relationship: 'Cluster Peer', strength: 91 },
      { address: 'Fx7M...Qy4L', relationship: 'Cluster Peer', strength: 87 },
      { address: 'Gy9N...Ra6P', relationship: 'Cluster Peer', strength: 95 },
      { address: 'Hz2P...Sb8L', relationship: 'Cluster Peer', strength: 89 },
      { address: 'Ax4P...Mn7Q', relationship: 'Cross-Cluster', strength: 31 },
    ],
    recentTransactions: [
      { date: '3d ago', type: 'Buy', token: 'FARTCOIN', amount: '67.2K', pnl: '+$18.4K' },
      { date: '8d ago', type: 'Buy', token: 'BONK', amount: '22.1K', pnl: '+$5.2K' },
      { date: '12d ago', type: 'Sell', token: 'JUP', amount: '12.4K', pnl: '+$3.8K' },
      { date: '18d ago', type: 'Buy', token: 'RENDER', amount: '8.4K', pnl: '+$2.1K' },
      { date: '24d ago', type: 'Sell', token: 'HNT', amount: '15.3K', pnl: '+$4.5K' },
    ],
    riskIndicators: ['Dormancy pattern', 'Deep conviction', 'CEX linked', 'Long hold pattern'],
    behavioralAnalysis: {
      tradingStyle: 'Deep conviction with multi-week holds',
      timePreference: 'Off-peak hours (UTC 04:00-08:00)',
      tokenConcentration: '62% in FARTCOIN ecosystem',
      avgEntrySize: '$14.2K',
      largestWin: '$42.1K',
      largestLoss: '-$8.4K',
      profitFactor: 4.2,
      sharpeRatio: 2.89,
    },
    network: {
      nodes: [
        { id: 'Ep7B...Gd4S', label: 'Ep7B...Gd4S', type: 'frequent' },
        { id: 'fw-binance', label: 'Binance Hot', type: 'funding' },
        { id: 'Dt3K...Nv9P', label: 'Dt3K...Nv9P', type: 'cluster-member', pnl: '+$52.1K', winRate: 84 },
        { id: 'Gy9N...Ra6P', label: 'Gy9N...Ra6P', type: 'cluster-member', pnl: '+$67.9K', winRate: 91 },
        { id: 'Ax4P...Mn7Q', label: 'Ax4P...Mn7Q', type: 'counterparty', pnl: '+$582.1K', winRate: 84 },
      ],
      edges: [
        { source: 'fw-binance', target: 'Ep7B...Gd4S' },
        { source: 'Ep7B...Gd4S', target: 'Dt3K...Nv9P' },
        { source: 'Ep7B...Gd4S', target: 'Gy9N...Ra6P' },
        { source: 'Ep7B...Gd4S', target: 'Ax4P...Mn7Q' },
      ],
    },
  },
};

// ── Dataset builder ──

function buildDataset(
  id: string,
  token: TokenInfo,
  clusters: ClusterDefinition[],
  graphNodes: GraphNodeDef[],
  graphEdges: GraphEdgeDef[],
  fundingWallets: FundingWalletNodeDef[],
  fundingTrails: FundingTrailDef[],
  tryTokens: string[],
  tryWallets: string[],
  landingStats: Dataset['landingStats'],
  walletIntel: Record<string, WalletIntelligenceData>,
): Dataset {
  return {
    id,
    token,
    clusters,
    clusterMap: buildClusterMap(clusters),
    graphNodes,
    graphNodeMap: buildGraphNodeMap(graphNodes),
    graphEdges,
    fundingWalletNodes: fundingWallets,
    fundingWalletMap: buildFundingWalletMap(fundingWallets),
    fundingTrails,
    tryTokens,
    tryWallets,
    landingStats,
    walletIntelligenceMap: walletIntel,
  };
}

export const ANSEM_DATASET = buildDataset(
  'ansem',
  { symbol: 'ANSEM', name: 'Ansem', price: '$2.84', change: '+8.7%' },
  ANSEM_CLUSTERS,
  ANSEM_GRAPH_NODES,
  ANSEM_GRAPH_EDGES,
  ANSEM_FUNDING_WALLETS,
  ANSEM_FUNDING_TRAILS,
  ['ANSEM', 'FARTCOIN'],
  ['8JdP...Xa92', 'Gj8k...F2Qa'],
  {
    capitalTraced: { value: 2.4, prefix: '$', suffix: 'M+', decimals: 1, label: 'CAPITAL TRACED' },
    walletsMapped: { value: 31, prefix: '', suffix: '', decimals: 0, label: 'WALLETS MAPPED' },
    activeClusters: { value: 5, prefix: '', suffix: '', decimals: 0, label: 'ACTIVE CLUSTERS' },
    avgConfidence: { value: 92, prefix: '', suffix: '%', decimals: 0, label: 'AVG CONFIDENCE' },
  },
  ANSEM_WALLET_INTEL,
);

export const FARTCOIN_DATASET = buildDataset(
  'fartcoin',
  { symbol: 'FARTCOIN', name: 'Fartcoin', price: '$0.0012', change: '+34.2%' },
  FARTCOIN_CLUSTERS,
  FARTCOIN_GRAPH_NODES,
  FARTCOIN_GRAPH_EDGES,
  FARTCOIN_FUNDING_WALLETS,
  FARTCOIN_FUNDING_TRAILS,
  ['FARTCOIN', 'ANSEM'],
  ['Ax4P...Mn7Q', 'Ep7B...Gd4S'],
  {
    capitalTraced: { value: 3.8, prefix: '$', suffix: 'M+', decimals: 1, label: 'CAPITAL TRACED' },
    walletsMapped: { value: 30, prefix: '', suffix: '', decimals: 0, label: 'WALLETS MAPPED' },
    activeClusters: { value: 5, prefix: '', suffix: '', decimals: 0, label: 'ACTIVE CLUSTERS' },
    avgConfidence: { value: 91, prefix: '', suffix: '%', decimals: 0, label: 'AVG CONFIDENCE' },
  },
  FARTCOIN_WALLET_INTEL,
);

export async function getDataset(token: string): Promise<Dataset> {
  const t = token.toUpperCase();
  if (t === 'FARTCOIN') return FARTCOIN_DATASET;
  if (t === 'ANSEM') return ANSEM_DATASET;

  // Check if it looks like a Solana address (44 chars, base58-like)
  const isAddress = /^[A-HJ-NP-Za-km-z1-9]{43,45}$/.test(token);
  if (isAddress) {
    const dexInfo = await fetchDexScreenerToken(token);
    if (dexInfo) {
      return generateGenericDataset({
        symbol: dexInfo.symbol,
        name: dexInfo.name,
        price: dexInfo.price,
        change: dexInfo.change,
        logo: dexInfo.logo,
        marketCap: dexInfo.marketCap,
        liquidity: dexInfo.liquidity,
        volume24h: dexInfo.volume24h,
      });
    }
    return generateGenericDataset({ symbol: token.slice(0, 6), name: 'Unknown Token', price: '$0.00', change: '0.0%' });
  }

  return generateGenericDataset({ symbol: t, name: t, price: '$0.00', change: '0.0%' });
}

export function getWalletIntelligence(dataset: Dataset, address: string): WalletIntelligenceData {
  const exact = dataset.walletIntelligenceMap[address];
  if (exact) return exact;
  return buildWalletIntelligenceFromTemplate(address, dataset.token.symbol);
}

export function loadingStepsFor(mode: 'token' | 'wallet', label: string): string[] {
  if (mode === 'token') {
    return [
      `Identifying ${label} traders...`,
      'Analyzing timing correlations...',
      'Detecting coordinated clusters...',
      'Mapping funding relationships...',
      'Rendering network graph...',
    ];
  }
  return [
    'Identifying wallet activity...',
    'Building wallet profile...',
    'Detecting connected wallets...',
    'Mapping funding relationships...',
    'Calculating wallet performance...',
    'Finalizing analysis...',
  ];
}

// ── Capital Trace helpers ──

export function parseTrailAmount(amount: string): number {
  const num = parseFloat(amount.replace(/[^0-9.]/g, ''));
  if (amount.includes('K')) return num * 1000;
  if (amount.includes('M')) return num * 1_000_000;
  return num;
}
